import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Box, Button, Typography, MenuItem, TextField, LinearProgress, Skeleton, Tooltip } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { ConfirmationModal } from '../../../../components/confirmationModal';
import {
  initialInvoiceValues,
  mapConcessionReasonsToOptions,
  validationInvoiceSchema,
  useUnbilledInvoiceServices,
  useUnbilledServicesByPatient,
  usePackageCloserServices,
  useBillingConfigOptions,
  mapUnbilledServicesToInvoiceServices,
  useActivePackages,
  useCliniciansByRole,
  Role,
  useCreateInvoice,
  PatientSourceWithTariffs,
  buildCreateInvoiceParams,
  useInvoiceDetails,
  mapInvoiceServicesToInvoiceServices,
  useUpdateInvoice,
  buildUpdateInvoiceParams,
  useCreatePayment,
  buildCreatePaymentParams,
  useValidatePackageAdvance,
  buildValidatePackageAdvanceParams,
  fetchLinkedRecipients,
  markSettlementSource,
  assignToRecipient,
  ActivePackageItem,
} from './newInvoiceForm.helper';
import { InvoiceFormValues, Service, Option, UnbilledInvoiceService } from './newInvoice.types';
import ServicesList from './ServicesList';
import PatientSourceSelect from './PatientSourceSelect';
import TariffSelect from './TariffSelect';
import SummaryCard from './SummaryCard';
import PackageCloserSummaryCard from './PackageCloserSummaryCard';
import ServiceSearchField from './ServiceSearchField/ServiceSearchField';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useClinic } from '../../../../components/ClinicProvider';
import { usePatientContext } from '../../../../components/patinetProvider';
import PackageServicesModal, { PackageServiceRow, CurrentPackageService } from './PackageServicesModal';
import CollectPackageAdvanceModal, { PackageOption, PackageAdvanceFormData } from './CollectPackageAdvanceModal';
import InsufficientAdvancePopup from './InsufficientAdvancePopup';
import RecipientSelectionModal, { Recipient } from './RecipientSelectionModal';
import DonorPackageSelect from './DonorPackageSelect';
import { Formik } from 'formik';
import { CollectPaymentModal, PaymentDetails, PaymentFormValues, PaymentHistoryItem, buildPaymentApiPayload, WalletAdvance } from '../../collectPayment';
import useCurrency from '../../../../components/CurrencyProvider';
import { useAlert } from '../../../../components/alertProvider';
import { postRequest } from '../../../../services/httpService';
import { postMakeAdvancePaymentURL, MakePayment, PaymentDetailPayload } from '../../Advance/advance.helper';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDateYMD, formatDateDMY, toISODateString, getTodayYMD } from '../../../../utils/dateUtils';
import { outlinedGrayButtonSx } from '../../../../utils/typographyStyles';
import { RequiredLabel } from '../../../../utils/commonUtils';

/**
 * =============================================================================
 * NewInvoiceForm Component
 * =============================================================================
 * 
 * This component handles the creation and editing of patient invoices.
 * 
 * KEY FLOWS:
 * 
 * 1. REGULAR PAYMENT FLOW (Non-Package Services)
 *    - User selects services → Clicks "Collect Payment" → Payment modal opens
 * 
 * 2. PACKAGE PAYMENT FLOW (Package-Related Services)
 *    When services are linked to a patient's active package:
 *    
 *    a) SUFFICIENT ADVANCE:
 *       - User clicks "Collect Payment"
 *       - API validates package advance (validatePackageAdvance)
 *       - If overall_valid === true → Payment modal opens with wallet pre-filled
 *    
 *    b) INSUFFICIENT ADVANCE:
 *       - User clicks "Collect Payment"
 *       - API validates package advance
 *       - If overall_valid === false → "Insufficient Advance" popup appears
 *       - User clicks "Collect Package Advance" → Opens advance collection flow
 * 
 * 3. COLLECT PACKAGE ADVANCE FLOW (3-Step Process)
 *    Step 1: CollectPackageAdvanceModal - User selects package & enters advance amount
 *    Step 2: CollectPaymentModal - User selects payment method(s)
 *    Step 3: API saves the advance payment
 * 
 * 4. PACKAGE INVOICE CLOSE FLOW
 *    - For bill_type === 'Package' with can_close_package_invoice === true
 *    - Shows "Close Package" button instead of "Collect Payment"
 *    - Marks invoice as paid without additional payment collection
 * 
 * =============================================================================
 */

/**
 * Effectful helper rendered inside Formik to auto-clear the concession reason
 * when no service has a concession. Using a component + useEffect avoids calling
 * setFieldValue during render, which can trigger React warnings in StrictMode.
 */
const ConcessionReasonAutoClear: React.FC<{
  hasConcession: boolean;
  concessionReason: string;
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
}> = ({ hasConcession, concessionReason, setFieldValue }) => {
  useEffect(() => {
    if (!hasConcession && concessionReason) {
      setFieldValue('concessionReason', '', false);
    }
  }, [hasConcession, concessionReason, setFieldValue]);
  return null;
};

const NewInvoiceForm = () => {
  /**
   * -----------------------------------------------------------------------
   * Local component state
   * -----------------------------------------------------------------------
   */
  const [patientSourceOptions, setPatientSourceOptions] = useState<PatientSourceWithTariffs[]>([]);
  // we will not keep a global "all tariffs"; tariffs depend on selected source
  const [services, setServices] = useState<Service[]>([]);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [submitAction, setSubmitAction] = useState<'draft' | 'finalize'>('draft');
  const [concessionReasonApiOptions, setConcessionReasonApiOptions] = useState<Option[]>([]);
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<number | string | null>(null);
  const [selectedPackageName, setSelectedPackageName] = useState<string>('');
  const [selectedDonorPackage, setSelectedDonorPackage] = useState<ActivePackageItem | null>(null);
  
  // Collect Payment Modal state
  const [collectPaymentOpen, setCollectPaymentOpen] = useState(false);
  const [paymentDetailsForModal, setPaymentDetailsForModal] = useState<PaymentDetails | null>(null);
  const [formValuesForPayment, setFormValuesForPayment] = useState<InvoiceFormValues | null>(null);
  const [paymentModes, setPaymentModes] = useState<{ id: number; name: string; code: string }[]>([]);
  const [walletAdvances, setWalletAdvances] = useState<WalletAdvance[]>([]);
  const [initialPaymentHistory, setInitialPaymentHistory] = useState<PaymentHistoryItem[]>([]);
  const [consumeFromPackageForModal, setConsumeFromPackageForModal] = useState<number>(0);

  // Draft confirmation dialog state
  const [draftConfirmOpen, setDraftConfirmOpen] = useState(false);
  const [freezeConfirmOpen, setFreezeConfirmOpen] = useState(false);
  // Package fully paid confirmation (when all services are from package with sufficient advance)
  const [packageFullPayConfirmOpen, setPackageFullPayConfirmOpen] = useState(false);
  const [canClosePackageInvoice, setCanClosePackageInvoice] = useState(false);
  const [billType, setBillType] = useState<string>('');
  // Remove service confirmation
  const [removeServiceConfirmOpen, setRemoveServiceConfirmOpen] = useState(false);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(null);  
  /**
   * -----------------------------------------------------------------------
   * Package Advance Collection State (3-Step Modal Flow)
   * -----------------------------------------------------------------------
   * 
   * When a patient's package has insufficient advance to cover selected services,
   * we need to collect additional advance before proceeding with payment.
   * 
   * STEP 1: CollectPackageAdvanceModal
   *   - Shows available packages for the patient
   *   - User selects package and enters advance amount
   *   - On submit → triggers handlePackageAdvanceCollectPayment()
   * 
   * STEP 2: CollectPaymentModal (for package advance)
   *   - Shows payment methods (cash, card, UPI, etc.)
   *   - User completes payment entry
   *   - On submit → triggers handleSavePackageAdvance()
   * 
   * STEP 3: API call to save advance (in handleSavePackageAdvance)
   *   - Combines package details with payment info
   *   - Calls API to record the advance
   */
  // Step 1 state: Package advance form modal
  const [collectPackageAdvanceOpen, setCollectPackageAdvanceOpen] = useState(false);
  const [packageOptionsForAdvance, setPackageOptionsForAdvance] = useState<PackageOption[]>([]);
  
  // Step 2 state: Payment methods modal (after advance form)
  const [collectPackagePaymentOpen, setCollectPackagePaymentOpen] = useState(false);
  const [packageAdvancePaymentDetails, setPackageAdvancePaymentDetails] = useState<PaymentDetails | null>(null);
  const [packageAdvanceFormData, setPackageAdvanceFormData] = useState<PackageAdvanceFormData | null>(null);
  
  // Insufficient advance popup - shown when package advance validation fails
  // Prompts user to collect additional advance before proceeding
  const [insufficientAdvanceOpen, setInsufficientAdvanceOpen] = useState(false);
  
  // Store validation result packages with required_amount for auto-populating advance modal
  const [validationResultPackages, setValidationResultPackages] = useState<
    { package_id: number; required_amount: number; balance_amount: number }[]
  >([]);
  
  /**
   * Ref to track latest Formik services outside of Formik render scope.
   * This is needed because some handlers (like handleOpenCollectPackageAdvanceExternal)
   * are called from outside Formik's render function and need access to current services.
   * Updated on every Formik render via: formikServicesRef.current = values.services;
   */
  const formikServicesRef = useRef<Service[]>([]);
  
  /**
   * Ref to track the packageId for which services have been loaded.
   * Prevents re-mapping services on subsequent refetches (e.g., after payment).
   * Only invoice_statistics should be updated after payment, not services.
   * Stores the packageId to allow reloading when navigating to a different package.
   */
  const packageCloserServicesLoadedRef = useRef<number | null>(null);
  
  /**
   * Ref to track modal transition timers for cleanup on unmount.
   * Prevents setState calls after component unmounts.
   */
  const modalTransitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { id: patientIdParam } = useParams<{ id: string }>();

  const patientId = patientIdParam ?? undefined;
  const { ClinicId } = useClinic();

  // Read appointment id, invoice id, package_id, bill_type, can_close_package_invoice, selectedServices, and mode from router state and patient id from URL 
  const { 
    appointment_id = null, 
    invoice_id = null, 
    package_id = null, 
    bill_type = null, 
    can_close_package_invoice = null,
    selectedServices = null,
    fromByServices = false,
  } =
    (location.state as { 
      appointment_id?: number | null; 
      invoice_id?: number | null;
      package_id?: number | null; 
      bill_type?: string | null;
      can_close_package_invoice?: boolean | null;
      selectedServices?: UnbilledInvoiceService[] | null;
      fromByServices?: boolean;
    } | null) ?? {};

  const isClosePackageMode = can_close_package_invoice === true;

  const appointmentId = appointment_id ?? undefined;
  const invoiceId = invoice_id ?? undefined;
  const packageId = package_id ?? undefined;

  const { addAlert } = useAlert();
  const queryClient = useQueryClient();
  
  /**
   * Mutation for submitting package advance payment
   * Uses the same API endpoint as Advance_Request module
   */
  const { mutate: submitAdvancePayment, isPending: isAdvanceSubmitting } = useMutation({
    mutationFn: (payload: MakePayment) => postRequest(postMakeAdvancePaymentURL(), payload),
    onSuccess: async () => {
      addAlert('Package advance collected successfully', 'success');
      setCollectPackagePaymentOpen(false);
      setPackageAdvanceFormData(null);
      setPackageAdvancePaymentDetails(null);
      // Invalidate active packages to refresh package advance data
      // Key must match useActivePackages hook: ['invoices', 'active-packages', patientId]
      await queryClient.invalidateQueries({ queryKey: ['invoices', 'active-packages'] });
      // Invalidate package closer services to refresh package summary details (only in close package mode)
      if (isClosePackageMode && packageId) {
        await queryClient.invalidateQueries({ queryKey: ['invoices', 'package-closer-services', packageId] });
      }
    },
    onError: (err: unknown) => {
      const message = (err as { message?: string })?.message || 'Failed to save advance payment.';
      addAlert(message, 'error');
    },
  });
  
  // Initialize billType from router state if available
  useEffect(() => {
    if (bill_type) {
      setBillType(bill_type);
    }
  }, [bill_type]);

  // Initialize canClosePackageInvoice from router state if available
  useEffect(() => {
    if (can_close_package_invoice !== null) {
      setCanClosePackageInvoice(can_close_package_invoice);
    }
  }, [can_close_package_invoice]);

  // eslint-disable-next-line no-console
  console.log('invoice_id------------', invoice_id);
  // eslint-disable-next-line no-console
  console.log('location.state------------', location.state);
  // eslint-disable-next-line no-console
  console.log('can_close_package_invoice from state------------', can_close_package_invoice);
  // eslint-disable-next-line no-console
  console.log('bill_type from state------------', bill_type);
  
  const { currency, currencySymbol, getInvoiceDueDate , maxBillCreditAmount} = useCurrency();
  
  // Calculate min (today) and max (today + invoice_due_days) for due date picker
  // Use Date object directly to avoid timezone issues from parsing YYYY-MM-DD strings
  const todayDate = getTodayYMD();
  const minDueDate = todayDate;
  const maxDueDate = formatDateYMD(getInvoiceDueDate(new Date()));
  
  // Get patient data from context
  const { MainpatientDetails } = usePatientContext();
  const currentPatient = MainpatientDetails;
  const patientFullName = currentPatient 
    ? `${currentPatient.first_name ?? ''} ${currentPatient.last_name ?? ''}`.trim() 
    : '';
  const patientMRN = currentPatient?.medical_record_no ?? '';

  // Donor/Surrogate type check for showing Continue with Donor / Continue with Recipient buttons
  const DONOR_SURROGATE_TYPES = ['SPERM_DONOR', 'OOCYTE_DONOR', 'SURROGATE'];
  const isDonorOrSurrogate = DONOR_SURROGATE_TYPES.includes(currentPatient?.patient_type ?? '');

  /**
   * When a donor selects a recipient's package, we pass `payer_id` and `payer_type`
   * alongside the normal `patient` (always the current patient).
   * If no payer is linked, the normal invoice flow applies.
   */
  const payerFromPackage = isDonorOrSurrogate
    && selectedDonorPackage?.patient_id != null
    && selectedDonorPackage.patient_id > 0
    ? selectedDonorPackage.patient_id
    : null;

  const getInvoicePatientFields = () => {
    const currentPatientId = patientId ? Number(patientId) : undefined;
    const fields: Record<string, unknown> = { patient: currentPatientId };
    if (payerFromPackage) {
      fields.payer_id = payerFromPackage;
      fields.payer_type = 'recipient';
    }
    return fields;
  };

  // Recipient selection modal state
  const [recipientModalOpen, setRecipientModalOpen] = useState(false);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [selectedRecipientId, setSelectedRecipientId] = useState<number | null>(null);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  // Store form values while recipient modal is open
  const [pendingFormValues, setPendingFormValues] = useState<InvoiceFormValues | null>(null);

  // local state for initial top-level values when editing
  const [loadedInvoiceInitials, setLoadedInvoiceInitials] = useState<Partial<InvoiceFormValues>>({});

  /**
   * -----------------------------------------------------------------------
   * API calls (TanStack Query)  
   * -----------------------------------------------------------------------
   */

  // Existing: unbilled services for new invoice flow
  const {
    data: unbilledData,
    isLoading: isServicesLoading,
    isError: isServicesError,
    error: servicesError,
  } = useUnbilledInvoiceServices(appointmentId ?? 0, patientId);

  // NEW: unbilled services by patient for "By Services" flow (only used when NOT passing services directly)
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    data: unbilledByPatientData,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isLoading: isUnbilledByPatientLoading,
  } = useUnbilledServicesByPatient(fromByServices && !selectedServices ? patientId : undefined);

  // NEW: load existing invoice details when invoiceId is present
  const {
    data: invoiceDetails,
    isLoading: isInvoiceLoading,
    isError: isInvoiceError,
    error: invoiceError,
  } = useInvoiceDetails(invoiceId);

  // NEW: load package closer services for close package flow
  const {
    data: packageCloserData,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isLoading: _isPackageCloserLoading,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isError: _isPackageCloserError,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    error: _packageCloserError,
  } = usePackageCloserServices(packageId);

  // Billing configuration options (e.g., concession reasons) – only when patient id is present
  const {
    data: billingConfigData,
    isLoading: isConfigLoading,
    isError: isConfigError,
    error: configError,
  } = useBillingConfigOptions(patientId);

  // Extract UPI providers from billing config (dynamic from API)
  const upiProviders = useMemo(() => {
    if (!billingConfigData) return undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const config = billingConfigData as Record<string, any>;
    const providers = config?.UPI_PROVIDER;
    if (!Array.isArray(providers) || providers.length === 0) return undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return providers.map((item: any) => ({ id: item.id, name: item.name, code: String(item.code) }));
  }, [billingConfigData]);

  const {
    data: activePackages,
    isLoading: isActivePackagesLoading,
    isError: isActivePackagesError,
    error: activePackagesError,
    refetch: refetchActivePackages,
  } = useActivePackages(
    isDonorOrSurrogate ? undefined : patientId ?? undefined,
    isDonorOrSurrogate ? patientId ?? undefined : undefined,
  );

  // Clinicians (doctors) for provider dropdown
  const {
    data: doctorListData,
    isLoading: isDoctorsLoading,
  } = useCliniciansByRole(ClinicId ?? 0, 'DOCTOR' as Role, !!ClinicId);
  // eslint-disable-next-line no-console
  console.error('isDoctorsLoading', isDoctorsLoading);
  

  // Temporary debug log for active packages API response
  // eslint-disable-next-line no-console
  console.error('Active packages for patient', patientId, activePackages, activePackagesError, isActivePackagesError);

  /**
   * -----------------------------------------------------------------------
   * Effects – initialize dropdown options & map API responses
   * -----------------------------------------------------------------------
   */

  // Cleanup modal transition timer on unmount
  useEffect(() => {
    return () => {
      if (modalTransitionTimerRef.current) {
        clearTimeout(modalTransitionTimerRef.current);
      }
    };
  }, []);

  // Unbilled flow (new invoice from appointment)
  useEffect(() => {
    if (!unbilledData?.services) return;
    if (invoiceId) return; // when editing, skip unbilled mapping
    if (packageId) return; // when in close package mode, skip unbilled mapping
    if (fromByServices) return; // when coming from "By Services" tab, skip this mapping
    let mapped = mapUnbilledServicesToInvoiceServices(unbilledData.services);
    // For donor/surrogate: strip pre-assigned package info so items load without packages.
    // The donor must pick a package from the dropdown to assign it.
    if (isDonorOrSurrogate) {
      mapped = mapped.map((svc) => ({
        ...svc,
        package: '',
        package_id: undefined,
        is_from_package: false,
      }));
    }
    setServices(mapped);
  }, [unbilledData, invoiceId, packageId, fromByServices, isDonorOrSurrogate]);

  // "By Services" flow (new invoice from patient's unbilled services selection)
  useEffect(() => {
    if (!fromByServices) return;
    if (!selectedServices || selectedServices.length === 0) return;
    
    // Use the directly passed selected services
    setServices(mapUnbilledServicesToInvoiceServices(selectedServices));
  }, [fromByServices, selectedServices]);

  // Close package flow (new invoice from package closer)
  // Only load services on initial fetch, not on subsequent refetches (e.g., after payment)
  // This prevents overwriting user edits when invoice_statistics is refreshed
  useEffect(() => {
    // Reset the ref when packageId becomes falsy (navigating away from close-package flow)
    // so future navigations can remap correctly
    if (!packageId) {
      packageCloserServicesLoadedRef.current = null;
      return;
    }
    if (!packageCloserData?.services) return;
    // Only set services if not already loaded for this packageId (prevents re-mapping after payment)
    // If packageId changes, reload services for the new package
    if (packageCloserServicesLoadedRef.current === packageId) return;
    setServices(mapUnbilledServicesToInvoiceServices(packageCloserData.services));
    packageCloserServicesLoadedRef.current = packageId;
  }, [packageId, packageCloserData]);

  // Set default due date for new invoices
  useEffect(() => {
    if (invoiceId) return; // skip for edit mode
    setLoadedInvoiceInitials(prev => ({
      ...prev,
      dueDate: maxDueDate,
    }));
  }, [invoiceId, maxDueDate]);

  // Edit flow (existing invoice)
  useEffect(() => {
    if (!invoiceId || !invoiceDetails) return;

    // Map services
    if (invoiceDetails.services?.length) {
      setServices(mapInvoiceServicesToInvoiceServices(invoiceDetails.services));
    }

    // Load can_close_package_invoice flag and bill_type only if not provided in router state
    // Router state takes precedence (from PatientPackageList navigation)
    if (can_close_package_invoice === null) {
      setCanClosePackageInvoice(invoiceDetails.can_close_package_invoice ?? false);
    }
    if (!bill_type) {
      setBillType(invoiceDetails.bill_type || '');
    }

    // Seed top-level fields from invoice
    setLoadedInvoiceInitials({
      // we currently don't have patientSource/tariff in response; keep defaults
      concessionReason: invoiceDetails.concession_reason ?? '',
      dueDate: invoiceDetails.due_date ?? '',
      termsAndConditions: invoiceDetails.terms_and_conditions ?? '',
      legalMentions: invoiceDetails.legal_mentions ?? '',
      remark: invoiceDetails.remark ?? '',
      comments: invoiceDetails.comments ?? '',
    });
  }, [invoiceId, invoiceDetails, can_close_package_invoice, bill_type]);

  // Map billing config -> dropdown option lists
  useEffect(() => {
    if (!billingConfigData) return;


    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const configData = billingConfigData as any;
    const { CONCESSION_REASON, tariff_source_group, modeofpayment } = configData;

    
    if (CONCESSION_REASON?.length) {
      setConcessionReasonApiOptions(
        mapConcessionReasonsToOptions(CONCESSION_REASON)
      );
    }

    if (tariff_source_group?.length) {
      setPatientSourceOptions(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tariff_source_group.map((item: any) => ({
          label: item.label ?? item.name,
          value: item.value ?? item.code,
          tariffs: item.tariffs ?? [],
        }))
      );
    }

    if (modeofpayment?.length) {
      setPaymentModes(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        modeofpayment.map((item: any) => ({
          id: item.id,
          name: item.name,
          code: String(item.code),
        }))
      );
    }
  }, [billingConfigData]);

  /**
   * -----------------------------------------------------------------------
   * Submit handler – decides between draft and collect
   * -----------------------------------------------------------------------
   */

  const createInvoiceMutation = useCreateInvoice();
  const updateInvoiceMutation = useUpdateInvoice();
  const createPaymentMutation = useCreatePayment();
  const validatePackageAdvanceMutation = useValidatePackageAdvance();
  
  // Combine all relevant mutation pending states for double-click prevention
  const isPaymentSubmitting = 
    createInvoiceMutation.isPending || 
    updateInvoiceMutation.isPending || 
    createPaymentMutation.isPending;

  // Shared helper: compute totals from selected services
  const computeInvoiceTotals = (svcList: Service[]) => {
    return svcList.reduce(
      (acc, s) => {
        if (!s.checked) return acc;
        const charges = Number(s.charges) || 0;
        const concession = Number(s.concession?.amount) || 0;
        const tax = Number(s.tax?.amount) || 0;
        const net = typeof s.net === 'number' ? s.net : charges - concession + tax;
        return {
          total: acc.total + charges,
          concession: acc.concession + concession,
          tax: acc.tax + tax,
          net: acc.net + net,
        };
      },
      { total: 0, concession: 0, tax: 0, net: 0 }
    );
  };

  const handleSubmitForm = (_values: InvoiceFormValues, options?: { shouldRedirect?: boolean }) => {
    const shouldRedirect = options?.shouldRedirect ?? false;
    
    // Map form services -> invoice items payload
    const itemsPayload = _values.services
      .filter((svc) => svc.checked) // only selected services
      .map((svc) => ({
        // For create we used generic mapping; for update we will re-map again into backend shape
        billing_source_type: svc.billing_source_type ?? 'SERVICE',
        billing_source_id: svc.billing_source_id ?? (svc.code && !isNaN(Number(svc.code)) ? Number(svc.code) : 0),
        billing_source_code: svc.billing_source_code ?? svc.code,
        billing_source_name: svc.billing_source_name ?? svc.service,
        // date: today or some visit date – here we use today's date in YYYY-MM-DD
        date: getTodayYMD(),
        provider: svc.provider ? Number(svc.provider) : null,
        charges: String(svc.charges ?? 0),
        concession_amount: String(svc.concession?.amount ?? 0),
        concession_percentage: String(svc.concession?.percent ?? 0),
        discount_amount: '0.00',
        tax_amount: String(svc.tax?.amount ?? 0),
        tax_percentage: String(svc.tax?.percent ?? 0),
        net_amount: String(svc.net?.toFixed(2) ?? 0),
        is_from_package: !!svc.package_id,
        is_from_consumption: svc.is_from_consumption ?? false,
        package_id: svc.package_id ?? null,
        package_name: svc.package || null,
        package_consumption_id: svc.package_consumption_id ?? null,
        bill_type: 'Clinical'
      }));

    // Basic guard: nothing selected -> do not call API
    if (!itemsPayload.length) {
      // eslint-disable-next-line no-console
      console.warn('No services selected, not creating/updating invoice');
      return;
    }
    
    // Compute header totals from selected services
    const headerTotals = computeInvoiceTotals(_values.services);
    
    const commonPayload = {
      ...getInvoicePatientFields(),
      visit_id: appointmentId ?? undefined,
      // For now, use today's date as visit_date; adjust if you have a real visit_date from API
      visit_date: getTodayYMD(),
      invoice_patient_type: isDonorOrSurrogate ? 'donor' : 'patient',
      // pick first doctor as treatment_doctor if not explicitly selected
      treatment_doctor:
        _values.services.find((s) => s.provider)?.provider
          ? Number(_values.services.find((s) => s.provider)!.provider)
          : doctorListData?.objects?.[0]?.id ?? null,
      concession_reason: _values.concessionReason || '',
      // Header totals
      total_amount: headerTotals.total.toFixed(2),
      balance_amount: headerTotals.net.toFixed(2),
      concession_amount: headerTotals.concession.toFixed(2),
      discount_amount: headerTotals.concession.toFixed(2),
      tax_amount: headerTotals.tax.toFixed(2),
      net_amount: headerTotals.net.toFixed(2),
      remark: _values.remark || '',
      terms_and_conditions: _values.termsAndConditions || '',
      legal_mentions: _values.legalMentions || '',
      comments: _values.comments || '',
      due_date: _values.dueDate || null,
      // status depends on which button the user clicked
      status: submitAction === 'draft' ? 'draft' : 'finalize',
      bill_type: 'Clinical'
    };

    if (invoiceId) {
      // UPDATE existing invoice: PUT /invoices/:id/
      const updateBody = {
        id: invoiceId,
        services: itemsPayload,
        ...commonPayload,
      };

      updateInvoiceMutation
        .mutateAsync(buildUpdateInvoiceParams(invoiceId, updateBody))
        .then((updatedInvoice) => {
          // eslint-disable-next-line no-console
          console.log('Invoice updated:', updatedInvoice);
          addAlert('Invoice updated successfully.', 'success');
          if (shouldRedirect) {
            navigate('/admin/billing/invoice/patient-invoice' + (patientId ? '/' + patientId : ''));
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('Failed to update invoice', err);
          addAlert('Failed to update invoice. Please try again.', 'error');
        });
    } else {
      // CREATE new invoice: POST /invoices/
      const createBody = {
        ...commonPayload,
        services: itemsPayload,
      };

      createInvoiceMutation
        .mutateAsync(buildCreateInvoiceParams(createBody))
        .then((createdInvoice) => {
          // eslint-disable-next-line no-console
          console.log('Invoice created:', createdInvoice);
          addAlert('Invoice saved successfully.', 'success');
          if (shouldRedirect) {
            navigate('/admin/billing/invoice/patient-invoice' + (patientId ? '/' + patientId : ''));
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('Failed to create invoice', err);
          addAlert('Failed to save invoice. Please try again.', 'error');
        });
    }
  };

  /**
   * Handle "Continue with Recipient" → Proceed flow.
   * Creates the invoice, then calls mark-settlement-source API.
   * Does NOT open the payment popup.
   */
  const handleRecipientProceed = async (recipientId?: number) => {
    if (!pendingFormValues) return;

    const _values = pendingFormValues;
    const itemsPayload = _values.services
      .filter((svc) => svc.checked)
      .map((svc) => ({
        billing_source_type: svc.billing_source_type ?? 'SERVICE',
        billing_source_id: svc.billing_source_id ?? (svc.code && !isNaN(Number(svc.code)) ? Number(svc.code) : 0),
        billing_source_code: svc.billing_source_code ?? svc.code,
        billing_source_name: svc.billing_source_name ?? svc.service,
        date: getTodayYMD(),
        provider: svc.provider ? Number(svc.provider) : null,
        charges: String(svc.charges ?? 0),
        concession_amount: String(svc.concession?.amount ?? 0),
        concession_percentage: String(svc.concession?.percent ?? 0),
        discount_amount: '0.00',
        tax_amount: String(svc.tax?.amount?.toFixed(2) ?? 0),
        tax_percentage: String(svc.tax?.percent?.toFixed(2) ?? 0),
        net_amount: String(svc.net?.toFixed(2) ?? 0),
        is_from_package: !!svc.package_id,
        is_from_consumption: svc.is_from_consumption ?? false,
        package_id: svc.package_id ?? null,
        package_name: svc.package || null,
        package_consumption_id: svc.package_consumption_id ?? null,
        bill_type: 'Clinical',
      }));

    if (!itemsPayload.length) return;

    const headerTotals = computeInvoiceTotals(_values.services);
    const commonPayload = {
      ...getInvoicePatientFields(),
      visit_id: appointmentId ?? undefined,
      visit_date: getTodayYMD(),
      invoice_patient_type: 'donor',
      treatment_doctor:
        _values.services.find((s) => s.provider)?.provider
          ? Number(_values.services.find((s) => s.provider)!.provider)
          : doctorListData?.objects?.[0]?.id ?? null,
      concession_reason: _values.concessionReason || '',
      total_amount: headerTotals.total.toFixed(2),
      balance_amount: headerTotals.net.toFixed(2),
      concession_amount: headerTotals.concession.toFixed(2),
      discount_amount: 0,
      tax_amount: headerTotals.tax.toFixed(2),
      net_amount: headerTotals.net.toFixed(2),
      remark: _values.remark || '',
      terms_and_conditions: _values.termsAndConditions || '',
      legal_mentions: _values.legalMentions || '',
      comments: _values.comments || '',
      due_date: _values.dueDate || null,
      status: 'draft',
      bill_type: 'Clinical',
    };

    try {
      let finalInvoiceId: number | undefined;

      if (invoiceId) {
        await updateInvoiceMutation.mutateAsync(
          buildUpdateInvoiceParams(invoiceId, { id: invoiceId, services: itemsPayload, ...commonPayload })
        );
        finalInvoiceId = Number(invoiceId);
      } else {
        const createdInvoice = await createInvoiceMutation.mutateAsync(
          buildCreateInvoiceParams({ ...commonPayload, services: itemsPayload })
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const resp = createdInvoice as any;
        finalInvoiceId = resp?.id ?? resp?.invoice_id;
      }

      if (finalInvoiceId) {
        await markSettlementSource(finalInvoiceId, 'recipient');
        if (recipientId) {
          await assignToRecipient(finalInvoiceId, recipientId);
          addAlert('Invoice assigned to recipient successfully', 'success');
        } else {
          addAlert('Invoice saved as unsettled', 'success');
        }
      }

      setRecipientModalOpen(false);
      setPendingFormValues(null);
      navigate('/admin/billing/invoice/patient-invoice' + (patientId ? '/' + patientId : ''));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to create invoice or mark settlement source:', err);
      addAlert('Failed to process recipient invoice. Please try again.', 'error');
    }
  };

  /**
   * -----------------------------------------------------------------------
   * STEP 1 → STEP 2 TRANSITION
   * -----------------------------------------------------------------------
   * Called when user clicks "Collect Advance" in CollectPackageAdvanceModal.
   * Transitions from Step 1 (advance form) to Step 2 (payment methods).
   * 
   * @param data - Contains selectedPackageId, advanceAmount, date, remarks
   */
  const handlePackageAdvanceCollectPayment = useCallback((data: PackageAdvanceFormData) => {
    // Validate input data
    if (!data) {
      // eslint-disable-next-line no-console
      console.error('handlePackageAdvanceCollectPayment: No data provided');
      addAlert('Invalid advance data. Please try again.', 'error');
      return;
    }

    // Validate advance amount is positive
    if (!data.advanceAmount || data.advanceAmount <= 0) {
      addAlert('Please enter a valid advance amount greater than zero.', 'warning');
      return;
    }

    // Store the package advance form data for later use in Step 3
    setPackageAdvanceFormData(data);
    
    // Close Step 1 modal
    setCollectPackageAdvanceOpen(false);
    
    // Prepare payment details for Step 2 modal with fallbacks
    const paymentDetails: PaymentDetails = {
      date: formatDateDMY(),
      patientName: patientFullName || 'Patient',
      patientId: patientMRN || patientId || '',
      patientNumericId: patientId,
      currency: currency || 'INR',
      netAmount: data.advanceAmount,
    };
    
    setPackageAdvancePaymentDetails(paymentDetails);
    
    // Open Step 2 modal (payment methods) after brief delay for smooth transition
    // Clear any existing timer before setting a new one
    if (modalTransitionTimerRef.current) {
      clearTimeout(modalTransitionTimerRef.current);
    }
    modalTransitionTimerRef.current = setTimeout(() => {
      setCollectPackagePaymentOpen(true);
      modalTransitionTimerRef.current = null;
    }, 150);
  }, [patientFullName, patientId, patientMRN, currency, addAlert]);

  /**
   * -----------------------------------------------------------------------
   * STEP 3: SAVE PACKAGE ADVANCE 
   * -----------------------------------------------------------------------
   * Called when user completes payment in CollectPaymentModal (Step 2).
   * This is the final step that saves the package advance to the backend.
   * 
   * Uses the same API endpoint as Advance_Request module:
   * POST /api/payments/ with payment_against_type: 'ADVANCE'
   * 
   * @param data - Payment form values (payment mode selections, amounts)
   * @param history - Array of payment history items (method, amount, date, etc.)
   * 
   * Data available at this point:
   * - packageAdvanceFormData: { selectedPackageId, advanceAmount, date, remarks, ... }
   * - history: [{ method: 'Cash', amount: 1000, ... }, ...]
   */
  const handleSavePackageAdvance = useCallback((data: PaymentFormValues, history: PaymentHistoryItem[]) => {
    // Validate packageAdvanceFormData exists
    if (!packageAdvanceFormData) {
      // eslint-disable-next-line no-console
      console.error('handleSavePackageAdvance: packageAdvanceFormData is null');
      addAlert('Package advance data not found. Please try again.', 'error');
      setCollectPackagePaymentOpen(false);
      return;
    }

    // Validate payment history is not empty
    if (!history || history.length === 0) {
      addAlert('Please add at least one payment method.', 'warning');
      return;
    }

    // Calculate total payment amount from history
    const totalPaymentAmount = history.reduce((sum, item) => sum + (item.amount || 0), 0);
    
    // Validate payment amount matches advance amount
    if (Math.abs(totalPaymentAmount - packageAdvanceFormData.advanceAmount) > 0.01) {
      addAlert(
        `Payment amount (${totalPaymentAmount}) does not match advance amount (${packageAdvanceFormData.advanceAmount}).`,
        'warning'
      );
      return;
    }

    // Calculate balance amount (package amount - already collected advance)
    const balanceAmount = Math.max(
      (packageAdvanceFormData.packageAmount ?? 0) - (packageAdvanceFormData.packageAdvanceCollected ?? 0),
      0
    );

    // Build payment details array matching the Advance_Request format
    const paymentCurrency = currency || 'INR';

    const payment_details: PaymentDetailPayload[] = history.map((item) => {
      const base: PaymentDetailPayload = {
        payment_mode_id: item.payment_mode_id ?? 0,
        paid_amount: String(item.amount ?? 0),
        currency: paymentCurrency,
        number: null,
        date: toISODateString(item.date),
        bank_id: null,
        account_name: null,
        branch_name: null,
        ifsc_code: null,
        remarks: item.notes ?? packageAdvanceFormData.remark ?? null,
        cheque_number: null,
        cheque_date: null,
      };

      // Map transaction numbers based on payment method
      if (item.method?.toLowerCase().includes('bank') || 
          item.method?.toLowerCase().includes('online') || 
          item.method?.toLowerCase().includes('card') || 
          item.method?.toLowerCase().includes('upi')) {
        base.number = item.transactionNumber ?? item.cardTransactionNumber ?? null;
      }

      // Map UPI provider ID
      if (item.upiProviderId) {
        base.upi_provider_id = item.upiProviderId;
      }

      // Map cheque-specific fields
      if (item.method?.toLowerCase().includes('cheque')) {
        base.cheque_number = item.chequeNumber ?? null;
        base.cheque_date = item.chequeDate ? toISODateString(item.chequeDate) : null;
      }

      return base;
    });

    // Validate required IDs before creating payload
    if (!patientId || !ClinicId) {
      addAlert('Missing patient or clinic information. Please try again.', 'error');
      return;
    }

    // Build the advance payment payload matching Advance_Request format
    // For donor patients, use the recipient's patient_id from the package
    // (the package belongs to the recipient, so the advance is recorded against them)
    const advancePatientId = packageAdvanceFormData.recipientPatientId
      ? packageAdvanceFormData.recipientPatientId
      : Number(patientId);

    const payload: MakePayment = {
      invoice_id: null, // null for advance payments
      patient_id: advancePatientId,
      clinic_id: Number(ClinicId),
      payment_against_type: 'ADVANCE',
      advance_data: {
        advance_against: packageAdvanceFormData.advanceAgainst || 'Package Service',
        advance_type: 'PACKAGE',
        date: packageAdvanceFormData.advanceDate || getTodayYMD(),
        amount: totalPaymentAmount,
        remark: packageAdvanceFormData.remark || '',
        appointment_visit: appointmentId ?? 0,
        patient_package_id: packageAdvanceFormData.selectedPackageId,
        balance_amount: balanceAmount,
      },
      remarks: packageAdvanceFormData.remark || '',
      payment_details,
    };

    // Log for debugging (development only)
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('Package Advance Payment Payload:', payload);
    }

    // Submit the advance payment and return a promise so callers can await it
    return new Promise<void>((resolve, reject) => {
      submitAdvancePayment(payload, {
        onSuccess: () => resolve(),
        onError: (error) => reject(error),
      });
    });
  }, [packageAdvanceFormData, addAlert, currency, patientId, ClinicId, appointmentId, submitAdvancePayment]);

  /**
   * -----------------------------------------------------------------------
   * OPEN COLLECT PACKAGE ADVANCE MODAL (External Handler)
   * -----------------------------------------------------------------------
   * This handler is called from OUTSIDE Formik's render function:
   * - From InsufficientAdvancePopup's onConfirm callback
   * - From the "Collect Package Advance" button in the footer
   * 
   * WHY EXTERNAL?
   * The InsufficientAdvancePopup is rendered outside of Formik's render scope,
   * so it cannot access Formik's `values.services`. Instead, we use
   * `formikServicesRef.current` which is kept in sync on every Formik render.
   * 
   * FLOW:
   * 1. Call API to fetch fresh active packages data
   * 2. Map ALL packages to PackageOption format
   * 3. Open CollectPackageAdvanceModal (Step 1)
   */
  const handleOpenCollectPackageAdvanceExternal = useCallback(async () => {
    // Refetch active packages to get fresh data from API
    const { data: freshPackages } = await refetchActivePackages();
    
    // Validate that freshPackages data exists and is an array with items
    if (!freshPackages || !Array.isArray(freshPackages) || freshPackages.length === 0) {
      addAlert('No active packages found for this patient. Please create a package first.', 'warning');
      return;
    }

    // Map ALL packages from API response to PackageOption format
    // Include requiredAdvanceAmount from validation result if available
    const packageOptions: PackageOption[] = freshPackages.map((pkg) => {
      // Find matching validation result for this package
      const validationPkg = validationResultPackages.find((vp) => vp.package_id === pkg.id);
      // Calculate required advance: required_amount - balance_amount (what's still needed)
      const requiredAdvance = validationPkg 
        ? Math.max(0, validationPkg.required_amount - validationPkg.balance_amount)
        : undefined;
      
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('Package mapping:', {
          packageId: pkg.id,
          validationPkg,
          requiredAdvance,
          calculation: validationPkg ? `${validationPkg.required_amount} - ${validationPkg.balance_amount} = ${requiredAdvance}` : 'no validation data',
        });
      }
      return {
        id: pkg.id,
        name: pkg.package_name || 'Unknown Package',
        startDate: pkg.start_date ?? undefined,
        packageAmount: pkg.package_amount ?? 0,
        packageNetAmount: Number(pkg.package_net_amount ?? 0),
        packageConcessionAmount: Number(pkg.package_concession_amount ?? 0),
        totalServiceConcessionAmount: Number(pkg.total_service_concession_amount ?? 0),
        packageAdvanceCollected: pkg.package_advance_collected ?? 0,
        advanceCollected: pkg.advance_collected ?? 0,
        packageUsedAmount: pkg.package_used_amount ?? 0,
        packageBalanceAmount: pkg.package_balance_amount ?? 0,
        serviceRemainingAmount: (pkg.service_remaining_amount ?? 0) - Number(pkg.package_concession_amount ?? 0) - Number(pkg.total_service_concession_amount ?? 0),
        pharmacyRemainingAmount: pkg.pharmacy_remaining_amount ?? 0,
        serviceAmount: (pkg.service_amount ?? 0) - Number(pkg.package_concession_amount ?? 0) - Number(pkg.total_service_concession_amount ?? 0),
        pharmacyAmount: pkg.pharmacy_amount ?? 0,
        pharmacyComponentAmount: pkg.pharmacy_component_amount ?? 0,
        pharmacyComponentPercent: pkg.pharmacy_component_percent ?? 0,
        collectedPharmacyAdvanceAmount: pkg.collected_pharmacy_advance_amount ?? 0,
        remainingPharmacyAdvanceAmount: pkg.remaining_pharmacy_advance_amount ?? 0,
        requiredAdvanceAmount: requiredAdvance,
        recipientPatientId: pkg.patient_id ?? undefined,
      };
    });

    // Set packages and open Step 1 modal
    setPackageOptionsForAdvance(packageOptions);
    setCollectPackageAdvanceOpen(true);
  }, [refetchActivePackages, addAlert, validationResultPackages]);

  /**
   * Early error boundary for critical data (services)
   */
  if (isServicesError) {
    return <div>Error loading services: {servicesError?.message}</div>;
  }

  // Optional: handle invoice load error separately (non-breaking)
  if (isInvoiceError) {
    // eslint-disable-next-line no-console
    console.error('Error loading invoice details:', invoiceError);
  }


  /**
   * Handle payment save from modal
   */
  const handlePaymentSave = (data: PaymentFormValues, history: PaymentHistoryItem[]) => {
    if (!formValuesForPayment) {
      // eslint-disable-next-line no-console
      console.error('No form values available for payment');
      return;
    }

    const _values = formValuesForPayment;

    // Map form services -> invoice items payload (reusing handleSubmitForm logic)
    const itemsPayload = _values.services
      .filter((svc) => svc.checked)
      .map((svc) => ({
        billing_source_type: svc.billing_source_type ?? 'SERVICE',
        billing_source_id: svc.billing_source_id ?? (svc.code && !isNaN(Number(svc.code)) ? Number(svc.code) : 0),
        billing_source_code: svc.billing_source_code ?? svc.code,
        billing_source_name: svc.billing_source_name ?? svc.service,
        date: getTodayYMD(),
        provider: svc.provider ? Number(svc.provider) : null,
        charges: String(svc.charges ?? 0),
        concession_amount: String(svc.concession?.amount ?? 0),
        concession_percentage: String(svc.concession?.percent ?? 0),
        discount_amount: '0.00',
        tax_amount: String(svc.tax?.amount?.toFixed(2) ?? 0),
        tax_percentage: String(svc.tax?.percent?.toFixed(2) ?? 0),
        net_amount: String(svc.net?.toFixed(2) ?? 0),
        is_from_package: !!svc.package_id,
        is_from_consumption: svc.is_from_consumption ?? false,
        package_id: svc.package_id ?? null,
        package_name: svc.package || null,
        package_consumption_id: svc.package_consumption_id ?? null,
      }));

    if (!itemsPayload.length) {
      // eslint-disable-next-line no-console
      console.warn('No services selected, not creating/updating invoice');
      return;
    }

    // Compute header totals from selected services (for payment flow)
    const headerTotals = computeInvoiceTotals(_values.services);

    const commonPayload = {
      ...getInvoicePatientFields(),
      visit_id: appointmentId ?? undefined,
      visit_date: getTodayYMD(),
      invoice_patient_type: isDonorOrSurrogate ? 'donor' : 'patient',
      bill_type: 'Clinical',
      treatment_doctor:
        _values.services.find((s) => s.provider)?.provider
          ? Number(_values.services.find((s) => s.provider)!.provider)
          : doctorListData?.objects?.[0]?.id ?? null,
      concession_reason: _values.concessionReason || '',
      // Header totals
      total_amount: headerTotals.total.toFixed(2),
      balance_amount: headerTotals.net.toFixed(2),
      concession_amount: headerTotals.concession.toFixed(2),
      discount_amount: 0,
      tax_amount: headerTotals.tax.toFixed(2),
      net_amount: headerTotals.net.toFixed(2),
      remark: _values.remark || '',
      terms_and_conditions: _values.termsAndConditions || '',
      legal_mentions: _values.legalMentions || '',
      comments: _values.comments || '',
      due_date: _values.dueDate || null,
      status: 'draft',
    };

    const processPayment = (finalInvoiceId: number | undefined) => {
      // Build package advance payment details if there's consume from package amount
      // Group package services by package_id and calculate consumed amount per package
      const packagePayments: PaymentHistoryItem[] = [];
      
      if (consumeFromPackageForModal > 0) {
        // Get the "Advance" payment mode ID
        const advanceMode = paymentModes.find(
          (mode) => mode.name.toLowerCase() === 'advance' || mode.code.toLowerCase() === 'advance'
        );
        
        // Validate that advance mode exists - block submission if not found
        if (!advanceMode) {
          addAlert('Unable to process package advance: Advance payment mode not configured. Please contact administrator.', 'error');
          return;
        }
        const advancePaymentModeId = advanceMode.id;

        // Group package services by package_id
        const packageServiceMap = new Map<number, number>();
        _values.services
          .filter((svc) => svc.checked && svc.is_from_package && svc.package_id)
          .forEach((svc) => {
            const pkgId = svc.package_id!;
            const currentAmount = packageServiceMap.get(pkgId) || 0;
            packageServiceMap.set(pkgId, currentAmount + (svc.net ?? 0));
          });

        // Create payment history items for each package
        packageServiceMap.forEach((amount, packageId) => {
          if (amount > 0) {
            packagePayments.push({
              id: `pkg-${packageId}-${Date.now()}`,
              method: 'Package Advance',
              amount: amount,
              payment_mode_id: advancePaymentModeId,
              package_id: packageId,
              date: getTodayYMD(),
              notes: 'Deducted from package advance',
            });
          }
        });
      }

      // Combine package payments with regular payment history
      const combinedHistory = [...packagePayments, ...history];

      // Only call payment API if:
      // 1. Payment mode is credit (to record credit invoice), OR
      // 2. There are actual payment details in history (including package payments)
      const shouldCallPaymentAPI = data.paymentMode === 'credit' || (combinedHistory && combinedHistory.length > 0);
      
      if (!shouldCallPaymentAPI) {
        // Close modal and navigate
        setCollectPaymentOpen(false);
        setFormValuesForPayment(null);
        navigate('/admin/billing/invoice/patient-invoice' + (patientId ? '/' + patientId : ''));
        return;
      }

      // Build API payload - for credit mode, pass empty payment_details
      const paymentPatientId = Number(patientId) || 0;
      const payload = data.paymentMode === 'credit' 
        ? {
          invoice_id: Number(finalInvoiceId) || 0,
          patient_id: paymentPatientId,
          clinic_id: Number(ClinicId) || 0,
          payment_against_type: 'INVOICE',
          remarks: '',
          payment_details: [], // Empty for credit mode
          ...(payerFromPackage ? { payer_id: payerFromPackage, payer_type: 'recipient' } : {}),
        }
        : {
          ...buildPaymentApiPayload(
            combinedHistory,
            Number(finalInvoiceId) || 0,
            paymentPatientId,
            Number(ClinicId) || 0,
            currency,
            'INVOICE',
            '' // remarks 
          ),
          ...(payerFromPackage ? { payer_id: payerFromPackage, payer_type: 'recipient' } : {}),
        };
      // Call payment API
      createPaymentMutation
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .mutateAsync(buildCreatePaymentParams(payload as any))
        .then(async (paymentResponse) => {
          // eslint-disable-next-line no-console
          console.log('Payment created:', paymentResponse);
          // Invalidate active-packages so Advance Balance Amount refreshes
          await queryClient.invalidateQueries({ queryKey: ['invoices', 'active-packages'] });
          addAlert('Invoice saved successfully.', 'success');
          // After successful payment, close modal and navigate to patient list
          setCollectPaymentOpen(false);
          setFormValuesForPayment(null);
          navigate('/admin/billing/invoice/patient-invoice' + (patientId ? '/' + patientId : '')); // redirect to patient list
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('Failed to create payment', err);
          addAlert('Failed to save invoice. Please try again.', 'error');
        });
    };

    if (invoiceId) {
      // UPDATE existing invoice
      const updateBody = {
        id: invoiceId,
        services: itemsPayload,
        ...commonPayload,
      };

      updateInvoiceMutation
        .mutateAsync(buildUpdateInvoiceParams(invoiceId, updateBody))
        .then((updatedInvoice) => {
          // eslint-disable-next-line no-console
          console.log('Invoice updated:', updatedInvoice);
          processPayment(invoiceId);
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('Failed to update invoice before payment', err);
          addAlert('Failed to update invoice. Please try again.', 'error');
        });
    } else {
      // CREATE new invoice
      const createBody = {
        ...commonPayload,
        services: itemsPayload,
      };

      createInvoiceMutation
        .mutateAsync(buildCreateInvoiceParams(createBody))
        .then((createdInvoice) => {
          // eslint-disable-next-line no-console
          console.log('Invoice created:', createdInvoice);
          // Extract the new invoice ID from the response
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const invoiceResponse = createdInvoice as any;
          const finalInvoiceId = invoiceResponse?.id ?? invoiceResponse?.invoice_id;
          processPayment(finalInvoiceId);
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('Failed to create invoice before payment', err);
          addAlert('Failed to save invoice. Please try again.', 'error');
        });
    }
  };

  /**
   * Handle save when all services are from package with sufficient advance
   * This saves the invoice directly with package consumption (no additional payment needed)
   */
  const handlePackageFullPaySave = async () => {
    if (!formValuesForPayment) {
      // eslint-disable-next-line no-console
      console.error('No form values available for package full pay save');
      return;
    }

    const _values = formValuesForPayment;

    // Map form services -> invoice items payload
    const itemsPayload = _values.services
      .filter((svc) => svc.checked)
      .map((svc) => ({
        billing_source_type: svc.billing_source_type ?? 'SERVICE',
        billing_source_id: svc.billing_source_id ?? (svc.code && !isNaN(Number(svc.code)) ? Number(svc.code) : 0),
        billing_source_code: svc.billing_source_code ?? svc.code,
        billing_source_name: svc.billing_source_name ?? svc.service,
        date: getTodayYMD(),
        provider: svc.provider ? Number(svc.provider) : null,
        charges: String(svc.charges ?? 0),
        concession_amount: String(svc.concession?.amount ?? 0),
        concession_percentage: String(svc.concession?.percent ?? 0),
        discount_amount: '0.00',
        tax_amount: String(svc.tax?.amount?.toFixed(2) ?? 0),
        tax_percentage: String(svc.tax?.percent?.toFixed(2) ?? 0),
        net_amount: String(svc.net?.toFixed(2) ?? 0),
        is_from_package: !!svc.package_id,
        is_from_consumption: svc.is_from_consumption ?? false,
        package_id: svc.package_id ?? null,
        package_name: svc.package || null,
        package_consumption_id: svc.package_consumption_id ?? null,
      }));

    if (!itemsPayload.length) {
      // eslint-disable-next-line no-console
      console.warn('No services selected, not creating invoice');
      return;
    }

    // Compute header totals from selected services
    const headerTotals = computeInvoiceTotals(_values.services);

    const invoicePayload = {
      ...getInvoicePatientFields(),
      visit_id: appointmentId ?? undefined,
      visit_date: getTodayYMD(),
      invoice_patient_type: isDonorOrSurrogate ? 'donor' : 'patient',
      bill_type: 'Clinical',
      treatment_doctor:
        _values.services.find((s) => s.provider)?.provider
          ? Number(_values.services.find((s) => s.provider)!.provider)
          : doctorListData?.objects?.[0]?.id ?? null,
      concession_reason: _values.concessionReason || '',
      total_amount: headerTotals.total.toFixed(2),
      balance_amount: headerTotals.net.toFixed(2), // Will be reduced when package payment is recorded
      concession_amount: headerTotals.concession.toFixed(2),
      discount_amount: 0,
      tax_amount: headerTotals.tax.toFixed(2),
      net_amount: headerTotals.net.toFixed(2),
      remark: _values.remark || '',
      terms_and_conditions: _values.termsAndConditions || '',
      legal_mentions: _values.legalMentions || '',
      comments: _values.comments || '',
      due_date: _values.dueDate || null,
      status: 'draft', // Create as draft first
      services: itemsPayload,
    };

    try {
      let finalInvoiceId: number | undefined;

      if (invoiceId) {
        // UPDATE existing draft invoice
        const updateBody = {
          ...invoicePayload,
          id: invoiceId,
        };
        await updateInvoiceMutation.mutateAsync(buildUpdateInvoiceParams(invoiceId, updateBody));
        finalInvoiceId = invoiceId;
        // eslint-disable-next-line no-console
        console.log('Draft invoice updated:', invoiceId);
      } else {
        // CREATE new invoice
        const createdInvoice = await createInvoiceMutation.mutateAsync(buildCreateInvoiceParams(invoicePayload));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const invoiceResponse = createdInvoice as any;
        finalInvoiceId = invoiceResponse?.id ?? invoiceResponse?.invoice_id;
        // eslint-disable-next-line no-console
        console.log('Invoice created:', finalInvoiceId);
      }

      // Build package advance payments
      const packagePayments: PaymentHistoryItem[] = [];
      const advanceMode = paymentModes.find(
        (mode) => mode.name.toLowerCase() === 'advance' || mode.code.toLowerCase() === 'advance'
      );

      if (!advanceMode) {
        addAlert('Unable to process package advance: Advance payment mode not configured.', 'error');
        return;
      }

      // Group package services by package_id
      const packageServiceMap = new Map<number, number>();
      _values.services
        .filter((svc) => svc.checked && svc.is_from_package && svc.package_id)
        .forEach((svc) => {
          const pkgId = svc.package_id!;
          const currentAmount = packageServiceMap.get(pkgId) || 0;
          packageServiceMap.set(pkgId, currentAmount + (svc.net ?? 0));
        });

      // Create payment history items for each package
      packageServiceMap.forEach((amount, pkgId) => {
        if (amount > 0) {
          packagePayments.push({
            id: `pkg-${pkgId}-${Date.now()}`,
            method: 'Package Advance',
            amount: amount,
            payment_mode_id: advanceMode.id,
            package_id: pkgId,
            date: getTodayYMD(),
            notes: 'Deducted from package advance',
          });
        }
      });

      // Call payment API with package payments
      const paymentPayload = {
        ...buildPaymentApiPayload(
          packagePayments,
          Number(finalInvoiceId) || 0,
          Number(patientId) || 0,
          Number(ClinicId) || 0,
          currency,
          'INVOICE',
          ''
        ),
        ...(payerFromPackage ? { payer_id: payerFromPackage, payer_type: 'recipient' } : {}),
      };

      await createPaymentMutation.mutateAsync(buildCreatePaymentParams(paymentPayload as Parameters<typeof buildCreatePaymentParams>[0]));
      
      // Invalidate active-packages so Advance Balance Amount refreshes
      await queryClient.invalidateQueries({ queryKey: ['invoices', 'active-packages'] });

      addAlert('Invoice saved successfully with package payment.', 'success');
      setPackageFullPayConfirmOpen(false);
      setFormValuesForPayment(null);
      navigate('/admin/billing/invoice/patient-invoice' + (patientId ? '/' + patientId : ''));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to save invoice with package payment:', err);
      addAlert('Failed to save invoice. Please try again.', 'error');
    }
  };

  /**
   * -----------------------------------------------------------------------
   * Formik wrapper
   * -----------------------------------------------------------------------
   */

  return (
    <>
      <Formik<InvoiceFormValues>
        initialValues={{
          ...initialInvoiceValues,
          ...loadedInvoiceInitials, // override defaults when editing
          services,
        }}
        validationSchema={validationInvoiceSchema}
        enableReinitialize
        validateOnBlur={true}
        validateOnChange={false}
        onSubmit={(values, helpers) => {
          setShowValidationErrors(true);
          handleSubmitForm(values);
          helpers.setSubmitting(false);
        }}
      >
        {({ values, setFieldValue, handleSubmit, isSubmitting, errors, touched, validateForm }) => {
          // Keep ref in sync with latest Formik services for use in callbacks
          formikServicesRef.current = values.services;
          
          /**
           * -------------------------------------------------------------------
           * Per-service validation helpers
           * -------------------------------------------------------------------
           * We derive an array of booleans for each field (e.g. package)
           * to drive row-level error display in ServicesList.
           */
          const servicesErrorsArray =
            Array.isArray(errors.services) ? (errors.services as unknown[]) : [];
          const servicesTouchedArray =
            Array.isArray(touched.services) ? (touched.services as unknown[]) : [];

          /**
           * Returns an array of booleans indicating which service rows
           * should display an error for the given field.
           */
          const getServiceFieldErrors = (fieldName: string): boolean[] => {
            const result = values.services.map((svc, idx) => {
              const svcError = (servicesErrorsArray[idx] || {}) as Record<string, unknown>;
              const svcTouched = (servicesTouchedArray[idx] || {}) as Record<string, unknown>;

              // Formik has a validation error for this field
              const hasFormikError = !!svcError?.[fieldName];

              // Either global 'showValidationErrors' or field was individually touched
              const shouldShowError =
                showValidationErrors || !!svcTouched?.[fieldName];

              // NOTE (2025-12-08):
              // Previously, package selection was mandatory for any checked service:
              //
              //   const isEmptyRequiredField =
              //     fieldName === 'package' && !!svc.checked && !svc.package;
              //
              // Business requirement changed: package selection is now OPTIONAL.
              // We intentionally do NOT enforce this rule anymore and rely only on
              // Formik's schema errors (hasFormikError) for this field.

              const finalError =
                !!svc.checked && shouldShowError && hasFormikError;

              return finalError;
            });

            return result;
          };

          const packageErrors = getServiceFieldErrors('package');
          // eslint-disable-next-line no-console
          console.log('packageErrors (final):', packageErrors);

          // Map activePackages API into simple dropdown options
          const packageOptions = Array.isArray(activePackages)
            ? activePackages
              .map((pkg) => {
                const packageItem = pkg as { package_name?: string; name?: string };
                return packageItem.package_name ?? packageItem.name ?? '';
              })
              .filter((name: string) => !!name)
            : [];

          // Create a lookup map to find package_id by package name
          const packageLookup: Record<string, number> = {};
          if (Array.isArray(activePackages)) {
            activePackages.forEach((pkg) => {
              const packageItem = pkg as { id?: number; package_name?: string; name?: string };
              const pkgName = packageItem.package_name ?? packageItem.name ?? '';
              if (pkgName && packageItem.id) {
                packageLookup[pkgName] = packageItem.id;
              }
            });
          }

          const doctorOptions =
            doctorListData?.objects?.map((doc) => {
              const fullName = `${doc.first_name ?? ''} ${doc.last_name ?? ''}`.trim();
              return {
                value: String(doc.id),
                label: fullName || String(doc.id),
              };
            }) ?? [];

          // build tariff options from selected patientSource
          const currentSource = values.patientSource;
          const selectedSource = patientSourceOptions.find(
            (src) => src.value === currentSource
          );
          const tariffOptions: Option[] = selectedSource?.tariffs
            ? selectedSource.tariffs.map((t) => ({
              label: t.tariff_name,
              value: t.tariff_code,
            }))
            : [];

          // Auto-select first patient source and its first tariff once, without hooks
          if (!values.patientSource && patientSourceOptions.length > 0) {
            const firstSource = patientSourceOptions[0];
            // 'false' to avoid triggering validation on this programmatic change
            setFieldValue('patientSource', firstSource.value, false);
            // Also auto-select the first tariff from this source immediately
            if (firstSource.tariffs?.length > 0 && !values.tariff) {
              setFieldValue('tariff', firstSource.tariffs[0].tariff_code, false);
            }
          }

          // Auto-select first tariff for the current source once (for manual source changes)
          if (
            values.patientSource &&
            !values.tariff &&
            tariffOptions.length > 0
          ) {
            setFieldValue('tariff', tariffOptions[0].value, false);
          }

          // if selected tariff no longer valid for this source, clear it
          if (
            values.tariff &&
            !tariffOptions.some((t) => t.value === values.tariff)
          ) {
            setFieldValue('tariff', '');
          }

          const tariffPlaceholder =
            !currentSource
              ? 'Please assign a tariff plan by selecting a  source first'
              : tariffOptions.length === 0
                ? 'No tariff plans are configured for the selected source'
                : '';

          // Get selected tariff ID for service search
          const selectedTariff = selectedSource?.tariffs?.find(t => t.tariff_code === values.tariff);
          const selectedTariffId = selectedTariff?.tariff_id ?? null;

          /**
           * -------------------------------------------------------------------
           * Render 
           * -------------------------------------------------------------------
           */

          // -------------------------------------------------------------------
          // Totals for SummaryCard – derived from current Formik values
          // -------------------------------------------------------------------
          const totals = computeInvoiceTotals(values.services);

          const totalAmount = totals.total;
          const concessionAmount = totals.concession;
          const taxAmount = totals.tax;
          const netAmount = totals.net;

          // True when any checked service has a concession amount or percent > 0
          const hasConcession = values.services.some(
            (svc) =>
              svc.checked &&
              ((Number(svc.concession?.amount) || 0) > 0 ||
               (Number(svc.concession?.percent) || 0) > 0)
          );

          // True when at least one checked service has a concession that is NOT from a package
          const hasManualConcession = values.services.some(
            (svc) =>
              svc.checked &&
              !svc.is_from_package &&
              ((Number(svc.concession?.amount) || 0) > 0 ||
               (Number(svc.concession?.percent) || 0) > 0)
          );

          // Auto-clear concession reason when no concession is applied
          // (rendered as a component so the state update happens in useEffect,
          //  not during the Formik render pass)

          // Calculate consume from package amount (capped by service_remaining_amount)
          const packageServicesTotal = values.services
            .filter((svc) => svc.checked && svc.is_from_package)
            .reduce((sum, svc) => sum + (svc.net ?? 0), 0);
          
          // Get the service_remaining_amount from the active package
          // If multiple packages, sum up their service_remaining_amount
          //////////////////////////////////////////////////////////////////////////
          //
          // FIX: Show advance balance only for packages that have selected services
          // This ensures UI matches what validatePackageAdvance API will check
          //
          //////////////////////////////////////////////////////////////////////////
          // Get unique package IDs from selected services
          const selectedPackageIds = new Set(
            values.services
              .filter((svc) => svc.checked && svc.package_id)
              .map((svc) => svc.package_id)
          );
          
          // Get the service_remaining_amount only for packages with selected services
          const totalServiceRemaining = activePackages?.reduce((sum, pkg) => {
            if (selectedPackageIds.has(pkg.id)) {
              return sum + (pkg.service_remaining_amount ?? 0);
            }
            return sum;
          }, 0) ?? 0;
          
          // Sum advance balance only for packages with selected services
          const totalAdvanceBalance = activePackages?.reduce((sum, pkg) => {
            if (selectedPackageIds.has(pkg.id)) {
              return sum + (pkg.package_balance_amount ?? 0);
            }
            return sum;
          }, 0) ?? 0;
          
          // Cap the consume amount at the available service_remaining_amount
          const consumeFromPackageAmount = Math.min(packageServicesTotal, totalServiceRemaining);

          // Check if package limit is exceeded (for validation/blocking payment)
          // Skip this validation when billType is 'Package' (closing package invoice flow)
          const isPackageLimitExceeded = billType !== 'Package' && packageServicesTotal > 0 && totalServiceRemaining > 0 && packageServicesTotal > totalServiceRemaining;

          /**
           * Check if ALL selected services are from a package with sufficient advance
           * In this case, we can save directly without opening payment modal
           * Conditions:
           * 1. All selected services have package_id and is_from_package (all from package)
           * 2. Package has sufficient advance balance to cover the net amount
           * 3. Not in Package Closer mode (handled separately)
           */
          const selectedServices = values.services.filter(svc => svc.checked);
          const allServicesFromPackage = selectedServices.length > 0 && 
            selectedServices.every(svc => !!svc.package_id && svc.is_from_package);
          const hasAnyPackageService = selectedServices.some(svc => !!svc.package_id && svc.is_from_package);
          const isFullyCoveredByPackage = allServicesFromPackage && 
            billType !== 'Package' && 
            consumeFromPackageAmount >= netAmount && 
            totalAdvanceBalance >= consumeFromPackageAmount;

          /**
           * Handle opening the collect payment modal
           */
          const handleOpenCollectPayment = (
            netAmount: number, 
            patientName: string, 
            patientIdValue: string, 
            formValues: InvoiceFormValues, 
            walletBal?: number,
            consumeFromPkg?: number
          ) => {
            setPaymentDetailsForModal({
              date: formatDateDMY(),
              patientName: patientName || 'Patient',
              patientId: patientMRN || patientIdValue || '',
              patientNumericId: patientId,
              currency: currency,
              netAmount: netAmount,
            });
            setFormValuesForPayment(formValues); // Store form values for payment processing
            if (consumeFromPkg !== undefined) {
              setConsumeFromPackageForModal(consumeFromPkg);
            } else {
              setConsumeFromPackageForModal(0);
            }
            setCollectPaymentOpen(true);
          };

          /**
           * Handle freeze package invoice (close and mark as paid)
           * Uses the same create invoice API as draft, but with status = 'paid'
           * 
           * Settlement Logic:
           * - Credit = Available Advance Balance (Advance Paid - Consumed)
           * - Misc Total = Net amount of selected services
           * - Payable = max(0, Misc Total - Credit)
           * - Refund = max(0, Credit - Misc Total)
           * 
           * Scenarios:
           * 1. Misc > Credit → User pays (handled separately via payment modal)
           * 2. Misc < Credit → User gets refund (refund_amount_requested in payload)
           * 3. Misc = Credit → Nothing (no payment, no refund)
           */
          const handleFreezePackageInvoice = async () => {
            try {
              // Helper to round to 2 decimal places
              const round2 = (val: number) => Math.round(val * 100) / 100;

              // Compute header totals from selected services
              const headerTotals = computeInvoiceTotals(values.services);
              
              // Map form services -> invoice items payload
              const itemsPayload = values.services
                .filter((svc) => svc.checked)
                .map((svc) => ({
                  billing_source_type: svc.billing_source_type ?? 'SERVICE',
                  billing_source_id: svc.billing_source_id ?? (svc.code && !isNaN(Number(svc.code)) ? Number(svc.code) : 0),
                  billing_source_code: svc.billing_source_code ?? svc.code,
                  billing_source_name: svc.billing_source_name ?? svc.service,
                  date: getTodayYMD(),
                  provider: svc.provider ? Number(svc.provider) : null,
                  charges: String(round2(Number(svc.charges ?? 0))),
                  concession_amount: String(round2(Number(svc.concession?.amount ?? 0))),
                  concession_percentage: String(round2(Number(svc.concession?.percent ?? 0))),
                  discount_amount: '0.00',
                  tax_amount: String(round2(Number(svc.tax?.amount ?? 0))),
                  tax_percentage: String(round2(Number(svc.tax?.percent ?? 0))),
                  net_amount: String(round2(Number(svc.net ?? 0))),
                  is_from_package: !!svc.package_id,
                  is_from_consumption: svc.is_from_consumption ?? false,
                  package_id: svc.package_id ?? null,
                  package_name: svc.package || null,
                  package_consumption_id: svc.package_consumption_id ?? null,
                }));

              // Calculate settlement amounts based on the package closure formulas
              // Credit = Available Advance Balance (already calculated in API as Advance Paid - Consumed)
              const creditAmount = Number(packageCloserData?.invoice_statistics?.available_advance_amount) || 0;
              // Misc Total = Net amount of selected services (charges after concessions + tax)
              const miscTotal = round2(headerTotals.net);
              
              // Payable = max(0, Misc Total - Credit) - amount user needs to pay
              const payableAmount = Math.max(0, round2(miscTotal - creditAmount));
              // Refund = max(0, Credit - Misc Total) - amount user receives back
              const refundAmount = Math.max(0, round2(creditAmount - miscTotal));

              // Build payload same as draft invoice, but with status = 'paid' and bill_type = 'Package Closer'
              const invoicePayload = {
                patient: patientId ? Number(patientId) : undefined,
                visit_id: appointmentId ?? undefined,
                visit_date: getTodayYMD(),
                invoice_patient_type: 'patient',
                bill_type: 'Package Closer',
                package_id: packageId ?? undefined,
                treatment_doctor:
                  values.services.find((s) => s.provider)?.provider
                    ? Number(values.services.find((s) => s.provider)!.provider)
                    : doctorListData?.objects?.[0]?.id ?? null,
                concession_reason: values.concessionReason || '',
                total_amount: round2(headerTotals.total),
                balance_amount: payableAmount, // Balance user needs to pay (0 if refund scenario)
                concession_amount: round2(headerTotals.concession),
                discount_amount: 0,
                tax_amount: round2(headerTotals.tax),
                net_amount: miscTotal,
                refund_amount_requested: refundAmount, // Amount to refund (0 if payable scenario)
                payable_amount: payableAmount, // Amount user needs to pay (0 if refund scenario)
                remark: values.remark || '',
                terms_and_conditions: values.termsAndConditions || '',
                legal_mentions: values.legalMentions || '',
                comments: values.comments || '',
                due_date: values.dueDate || null,
                status: 'paid',
                services: itemsPayload,
              };

              // Use the same create invoice API as draft
              await createInvoiceMutation.mutateAsync(buildCreateInvoiceParams(invoicePayload));
              // eslint-disable-next-line no-console
              console.log('Package invoice closed successfully');
              
              // Show appropriate success message based on settlement type
              if (refundAmount > 0) {
                addAlert(`Package closed successfully. Refund of ${refundAmount.toLocaleString()} requested.`, 'success');
              } else if (payableAmount > 0) {
                addAlert(`Package closed successfully. Additional payment of ${payableAmount.toLocaleString()} recorded.`, 'success');
              } else {
                addAlert('Package closed successfully.', 'success');
              }
              
              navigate('/admin/billing/invoice/patient-invoice' + (patientId ? '/' + patientId : ''));
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error('Failed to close package invoice:', err);
              addAlert('Failed to close package', 'error');
            }
          };


          /**
           * Handle opening collect package advance modal (Step 1)
           * Calls the API fresh to get latest active packages data
           * 
           * For Package Closer flow (billType === 'Package'):
           *   - Uses Payable Amount (netAmount - available_advance_amount) as requiredAdvanceAmount
           *   - This auto-populates the advance amount field with what the patient needs to pay
           */
          const handleOpenCollectPackageAdvance = async () => {
            // Refetch active packages to get fresh data from API
            const { data: freshPackages } = await refetchActivePackages();
            
            // Use freshly fetched data
            if (!freshPackages || !Array.isArray(freshPackages) || freshPackages.length === 0) {
              addAlert('No active packages found for this patient', 'warning');
              return;
            }

            // Calculate Payable Amount for Package Closer flow
            // Payable Amount = Net Amount - Advance Balance Amount
            const availableAdvanceAmount = Number(packageCloserData?.invoice_statistics?.available_advance_amount) || 0;
            const payableAmount = billType === 'Package' 
              ? Math.max(0, netAmount - availableAdvanceAmount)
              : undefined;

            // Map ALL packages from API response to PackageOption format
            // Include requiredAdvanceAmount from validation result if available
            const packageOptions: PackageOption[] = freshPackages.map((pkg) => {
              // For Package Closer flow, use Payable Amount for the current package
              // For other flows, use validation result's required_amount
              let requiredAdvance: number | undefined;
              
              if (billType === 'Package' && packageId && pkg.id === Number(packageId) && payableAmount !== undefined) {
                // Package Closer flow: use Payable Amount from Summary Card
                requiredAdvance = payableAmount;
              } else {
                // Other flows: use validation result
                const validationPkg = validationResultPackages.find((vp) => vp.package_id === pkg.id);
                requiredAdvance = validationPkg 
                  ? Math.max(0, validationPkg.required_amount - validationPkg.balance_amount)
                  : undefined;
              }
              
              return {
                id: pkg.id,
                name: pkg.package_name,
                startDate: pkg.start_date ?? undefined,
                packageAmount: pkg.package_amount ?? 0,
                packageNetAmount: Number(pkg.package_net_amount ?? 0),
                packageConcessionAmount: Number(pkg.package_concession_amount ?? 0),
                totalServiceConcessionAmount: Number(pkg.total_service_concession_amount ?? 0),
                packageAdvanceCollected: pkg.package_advance_collected ?? 0,
                advanceCollected: pkg.advance_collected ?? 0,
                packageUsedAmount: pkg.package_used_amount ?? 0,
                packageBalanceAmount: pkg.package_balance_amount ?? 0,
                serviceRemainingAmount: (pkg.service_remaining_amount ?? 0) - Number(pkg.package_concession_amount ?? 0) - Number(pkg.total_service_concession_amount ?? 0),
                pharmacyRemainingAmount: pkg.pharmacy_remaining_amount ?? 0,
                serviceAmount: (pkg.service_amount ?? 0) - Number(pkg.package_concession_amount ?? 0) - Number(pkg.total_service_concession_amount ?? 0),
                pharmacyAmount: pkg.pharmacy_amount ?? 0,
                pharmacyComponentAmount: pkg.pharmacy_component_amount ?? 0,
                pharmacyComponentPercent: pkg.pharmacy_component_percent ?? 0,
                collectedPharmacyAdvanceAmount: pkg.collected_pharmacy_advance_amount ?? 0,
                remainingPharmacyAdvanceAmount: pkg.remaining_pharmacy_advance_amount ?? 0,
                requiredAdvanceAmount: requiredAdvance,
                recipientPatientId: pkg.patient_id ?? undefined,
              };
            });

            setPackageOptionsForAdvance(packageOptions);
            setCollectPackageAdvanceOpen(true);
          };

          // /**
          //  * Handle payment save from modal 
          //  */
          // const handlePaymentSave = (data: PaymentFormValues, history: PaymentHistoryItem[]) => {
          //   // eslint-disable-next-line no-console
          //   console.log('Payment saved:', data, history);
            
          //   // TODO: Call your API to save payment
          //   // After successful payment, you might want to navigate or show success message
          //   setCollectPaymentOpen(false);
          // };

          return (
            <form onSubmit={handleSubmit}>
              {/* Auto-clear concession reason when no manual concession is applied  */}
              <ConcessionReasonAutoClear
                hasConcession={hasManualConcession}
                concessionReason={values.concessionReason}
                setFieldValue={setFieldValue}
              />
              {/* Main services section header + filters/search */}
              <Box sx={{ mt: 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Grid2 container spacing={2} alignItems="center" justifyContent="flex-end">
                    <Grid2 size={{ xs: 12, md: isDonorOrSurrogate ? 1.5 : 4 }} sx={{ mr: 'auto' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Services
                      </Typography>
                    </Grid2>

                    {/* Patient Source */}
                    <Grid2 size={{ xs: 12, sm: 3, md: isDonorOrSurrogate ? 2 : 2.5 }}>
                      <PatientSourceSelect
                        options={patientSourceOptions}
                        value={values.patientSource}
                        onChange={(v) => {
                          // when source changes, clear tariff and set patientSource
                          setFieldValue('patientSource', v);
                          setFieldValue('tariff', '');
                        }}
                        error={errors.patientSource as string}
                        showErrors={showValidationErrors}
                        disabled={billType === 'Package'}
                      />
                    </Grid2>

                    {/* Tariff: shows only tariffs belonging to selected patient source */}
                    <Grid2 size={{ xs: 12, sm: 3, md: isDonorOrSurrogate ? 2 : 2.5 }}>
                      <TariffSelect
                        options={tariffOptions}
                        value={values.tariff}
                        onChange={(v) => setFieldValue('tariff', v)}
                        error={errors.tariff as string}
                        showErrors={showValidationErrors}
                        placeholder={tariffPlaceholder}
                        disabled={billType === 'Package'}
                      />
                    </Grid2>

                    {/* Service search & add — for donor: Add Package Item (left) + Add Non Package Item (right) */}
                    {isDonorOrSurrogate && (
                      <Grid2 size={{ xs: 12, sm: 4, md: 2.5 }}>
                        <DonorPackageSelect
                          packages={Array.isArray(activePackages) ? activePackages : []}
                          loading={isActivePackagesLoading}
                          disabled={billType === 'Package'}
                          value={selectedDonorPackage}
                          onSelect={(pkg) => {
                            setSelectedDonorPackage(pkg);
                            setSelectedPackageId(pkg.id);
                            setSelectedPackageName(pkg.package_name ?? pkg.name ?? '');

                            // Auto-assign the selected package to existing items that have matching package_service_rates
                            const pkgId = pkg.id;
                            const pkgName = pkg.package_name ?? pkg.name ?? '';
                            const currentServices = formikServicesRef.current;
                            const updatedServices = currentServices.map((svc) => {
                              // Check if this service has the selected package in its package_service_rates
                              const matchingRate = svc.package_service_rates?.find(
                                (r) => r.package === pkgId || r.name === pkgName
                              );
                              if (matchingRate) {
                                // Assign the package and update charges/concession from package rate
                                const baseCharges = Number(matchingRate.unit_rate) || svc.charges;
                                const discountAmount = Number(matchingRate.discount_amount ?? 0);
                                const discountPercent = baseCharges
                                  ? Number(matchingRate.discount_percent ?? (discountAmount / baseCharges) * 100) || 0
                                  : 0;
                                return {
                                  ...svc,
                                  package: pkgName,
                                  package_id: pkgId,
                                  is_from_package: true,
                                  charges: baseCharges,
                                  concession: { amount: discountAmount, percent: discountPercent },
                                  tax: { amount: 0, percent: 0 },
                                  net: baseCharges - discountAmount,
                                };
                              }
                              // If this item was previously assigned to a different package, clear it
                              // and reset pricing back to non-package state
                              if (svc.package_id && svc.package_id !== pkgId) {
                                // Try to find original (non-package) charges from the first tariff rate, or keep current
                                const originalCharges = svc.charges;
                                return {
                                  ...svc,
                                  package: '',
                                  package_id: undefined,
                                  is_from_package: false,
                                  is_from_consumption: false,
                                  concession: { amount: 0, percent: 0 },
                                  tax: { amount: 0, percent: 0 },
                                  net: originalCharges,
                                };
                              }
                              return svc;
                            });
                            setFieldValue('services', updatedServices);
                            setServices(updatedServices);
                          }}
                          onReopen={() => {
                            setPackageModalOpen(true);
                          }}
                          onClear={() => {
                            setSelectedDonorPackage(null);
                            setSelectedPackageId('');
                            setSelectedPackageName('');
                            // Strip package assignments from all services
                            const currentServices = formikServicesRef.current;
                            const updatedServices = currentServices.map((svc) => {
                              if (svc.package_id || svc.is_from_package) {
                                return {
                                  ...svc,
                                  package: '',
                                  package_id: undefined,
                                  is_from_package: false,
                                  is_from_consumption: false,
                                  concession: { amount: 0, percent: 0 },
                                  tax: { amount: 0, percent: 0 },
                                  net: svc.charges,
                                };
                              }
                              return svc;
                            });
                            setFieldValue('services', updatedServices);
                            setServices(updatedServices);
                          }}
                        />
                      </Grid2>
                    )}
                    <Grid2 size={{ xs: 12, md: 2.5 }}>
                      <ServiceSearchField
                        label={isDonorOrSurrogate ? 'Add Non Package Item' : undefined}
                        value={values.serviceSearch}
                        patientId={patientId}
                        tariffId={selectedTariffId || undefined}
                        onChange={(v: string) => setFieldValue('serviceSearch', v)}
                        disabled={billType === 'Package'}
                        onSelectService={(billingService) => {
                          // Check if service has package_service_rates and extract package info
                          const packageRates = billingService.package_service_rates;
                          const hasPackageRates = Array.isArray(packageRates) && packageRates.length > 0;
                          
                          // Use package info from the service or from package_service_rates
                          const defaultPackageRate = hasPackageRates ? packageRates[0] : null;
                          const packageName = billingService.package_name || defaultPackageRate?.name || '';
                          const packageId = billingService.package_id || defaultPackageRate?.package || undefined;
                          
                          // Calculate charges and discounts from package rate if available
                          const baseCharges = hasPackageRates && defaultPackageRate?.unit_rate != null
                            ? Number(defaultPackageRate.unit_rate)
                            : Number(billingService.charges) || 0;
                          const discountAmount = hasPackageRates && defaultPackageRate?.discount_amount != null
                            ? Number(defaultPackageRate.discount_amount)
                            : Number(billingService.discount_amount) || 0;
                          const discountPercent = hasPackageRates && defaultPackageRate?.discount_percent != null
                            ? Number(defaultPackageRate.discount_percent)
                            : Number(billingService.discount_percent) || 0;
                          const netAmount = baseCharges - discountAmount;

                          const newService: Service = {
                            date: getTodayYMD(),
                            code: billingService.code,
                            service: billingService.name,
                            package: packageName,
                            package_id: packageId,
                            is_from_package: !!packageId || billingService.is_from_package || false,
                            provider: billingService.provider != null ? String(billingService.provider) : billingService.provider_id != null ? String(billingService.provider_id) : '',
                            charges: baseCharges,
                            concession: { amount: discountAmount, percent: discountPercent },
                            tax: { amount: 0, percent: 0 },
                            net: netAmount,
                            checked: true,
                            isFromSearch: true,
                            billing_source_type: billingService.source_type || 'SERVICE',
                            billing_source_id: billingService.id,
                            billing_source_code: billingService.code,
                            billing_source_name: billingService.name,
                            package_service_rates: packageRates,
                          };

                          // Use ref to get the LATEST Formik services (not stale closure)
                          const currentServices = formikServicesRef.current;
                          const updatedServices = [...currentServices, newService];
                          // Update both Formik AND React state to prevent enableReinitialize from resetting
                          setFieldValue('services', updatedServices);
                          setServices(updatedServices);
                        }}
                      />
                    </Grid2>
                  </Grid2>
                </Box>

                {/* Services list with row-level validation */}
                {(isServicesLoading || isInvoiceLoading || isConfigLoading || (isActivePackagesLoading && !isActivePackagesError)) ? (
                  <Box sx={{ width: '100%' }}>
                    <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
                    {[1, 2, 3, 4].map((row) => (
                      <Box key={row} sx={{ display: 'flex', gap: 2, mb: 1.5, alignItems: 'center' }}>
                        <Skeleton variant="rectangular" width={24} height={24} sx={{ borderRadius: 0.5 }} />
                        <Skeleton variant="text" width="20%" height={32} />
                        <Skeleton variant="text" width="15%" height={32} />
                        <Skeleton variant="text" width="15%" height={32} />
                        <Skeleton variant="text" width="10%" height={32} />
                        <Skeleton variant="text" width="10%" height={32} />
                        <Skeleton variant="text" width="10%" height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                      </Box>
                    ))}
                  </Box>
                ) : isConfigError ? (
                  <Box sx={{ p: 2, textAlign: 'center', color: 'error.main' }}>
                    <Typography>Failed to load configuration. {configError?.message || 'Please try again.'}</Typography>
                  </Box>
                ) : (
                  <ServicesList
                    services={values.services}
                    onSelectChange={(idx: number) => {
                      const updated = [...values.services];
                      updated[idx].checked = !updated[idx].checked;
                      setFieldValue('services', updated);
                    }}
                    onRemoveService={(idx: number) => {
                      setPendingRemoveIndex(idx);
                      setRemoveServiceConfirmOpen(true);
                    }}
                    packageErrors={packageErrors} 
                    packageOptions={packageOptions}
                    onPackageChange={(idx: number, pkgName: string) => {
                      const updated = [...values.services];
                      const svcAtIdx = updated[idx];
                      svcAtIdx.package = pkgName;

                      let resolvedPackageId: number | undefined;
                      let selectedRateUnit: number | undefined;
                      let selectedDiscountAmount: number | undefined;
                      let selectedDiscountPercent: number | undefined;

                      if (pkgName) {
                        if (packageLookup[pkgName]) {
                          resolvedPackageId = packageLookup[pkgName];
                        }

                        const fromRates = svcAtIdx.package_service_rates?.find(
                          (r) => r.name === pkgName || r.package === resolvedPackageId
                        );

                        if (fromRates) {
                          resolvedPackageId = fromRates.package;
                          selectedRateUnit = fromRates.unit_rate;
                          selectedDiscountAmount = fromRates.discount_amount;
                          selectedDiscountPercent = fromRates.discount_percent;
                        }
                      }

                      if (resolvedPackageId) {
                        svcAtIdx.package_id = resolvedPackageId;
                        svcAtIdx.is_from_package = true;
                      } else {
                        svcAtIdx.package_id = undefined;
                        svcAtIdx.is_from_package = false;
                      }

                      // When package changes, update charges/discount/net from selected package rate
                      if (selectedRateUnit != null) {
                        const baseCharges = Number(selectedRateUnit) || 0;
                        const discountAmount = Number(selectedDiscountAmount ?? 0) || 0;
                        const discountPercent = baseCharges
                          ? Number(
                            selectedDiscountPercent ?? (discountAmount / baseCharges) * 100
                          ) || 0
                          : 0;

                        svcAtIdx.charges = baseCharges;
                        svcAtIdx.concession = {
                          amount: discountAmount,
                          percent: discountPercent,
                        };
                        // Reset tax when switching packages; user can re-enter if needed
                        svcAtIdx.tax = { amount: 0, percent: 0 };
                        svcAtIdx.net = baseCharges - discountAmount;
                      }

                      setFieldValue('services', updated);
                    }}
                    providerOptions={doctorOptions}
                    onProviderChange={(idx: number, providerId: string) => {
                      const updated = [...values.services];
                      updated[idx].provider = providerId;
                      setFieldValue('services', updated);
                    }}
                    onPackageInfoClick={(idx: number) => {
                      const svc = values.services[idx];
                      // THIS is where you pass REAL package_id from your API object
                      // svc.package_id came from the response:
                      //   "package_id": 11,
                      setSelectedPackageId(svc.package_id ?? '');
                      setSelectedPackageName(svc.package || '');
                      setPackageModalOpen(true);
                    }}
                    isClosePackageMode={canClosePackageInvoice}
                  />
                )}
              </Box>

              {/* Bottom section: concession, due date, notes, summary */}
              <Grid2 container spacing={2} sx={{ mt: 2 }}>
                <Grid2 size={{ xs: 12, md: 8 }}>
                  {/* Concession + Due Date row */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>

                    <Box sx={{ flex: 1 }}>
                      <Tooltip
                        title={!hasConcession ? 'No concession applied to any service' : hasConcession && !hasManualConcession ? 'Concession is applied from package' : ''}
                        arrow
                        placement="top"
                        disableHoverListener={hasManualConcession}
                      >
                        <span style={{ display: 'block', width: '100%' }}>
                          <TextField
                            fullWidth
                            select
                            size="small"
                            label="Concession Reason"
                            value={values.concessionReason}
                            onChange={e => {
                              // Pass true as 3rd param to trigger validation after value is set
                              setFieldValue('concessionReason', e.target.value, true);
                            }}
                            disabled={!hasManualConcession}
                            error={!!(showValidationErrors && errors.concessionReason)}
                            slotProps={{ inputLabel: { shrink: !!values.concessionReason || undefined } }}
                          >
                            <MenuItem disabled value="">
                              Select reason
                            </MenuItem>
                            {concessionReasonApiOptions.map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </span>
                      </Tooltip>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <TextField
                        fullWidth
                        type="date"
                        label={RequiredLabel('Due Date', true)}
                        value={values.dueDate}
                        onChange={e => setFieldValue('dueDate', e.target.value)}
                        size="small"
                        slotProps={{
                          inputLabel: { shrink: true },
                          htmlInput: { min: minDueDate, max: maxDueDate },
                        }}
                        error={!!(showValidationErrors && errors.dueDate && !values.dueDate)}
                      />
                    </Box>
                  </Box>

                  {/* Terms and conditions */}
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Terms and Conditions"
                      value={values.termsAndConditions}
                      onChange={e => setFieldValue('termsAndConditions', e.target.value)}
                      placeholder="Ex. Payments can be made via cash, UPI, card, net banking, or bank transfer. Full payment is due at the time of service unless a credit facility is approved. Refunds, if applicable, will be processed within 7–10 working days."
                      size="small"
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </Box>

                  {/* Legal mentions */}
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Legal Mentions"
                      value={values.legalMentions}
                      onChange={e => setFieldValue('legalMentions', e.target.value)}
                      placeholder="Ex. Medical services may be tax-exempt under applicable laws. Patient details are confidential and protected under privacy regulations. All billing disputes must be reported within 7 days of invoice generation."
                      size="small"
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </Box>

                  {/* Remarks & comments */}
                  <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Remark"
                        placeholder="Type Here.."
                        value={values.remark}
                        onChange={e => setFieldValue('remark', e.target.value)}
                        size="small"
                        slotProps={{ inputLabel: { shrink: !!values.remark || undefined } }}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Comments"
                        placeholder="Type Here..."
                        value={values.comments}
                        onChange={e => setFieldValue('comments', e.target.value)}
                        size="small"
                        slotProps={{ inputLabel: { shrink: !!values.comments || undefined } }}
                      />
                    </Grid2>
                  </Grid2>
                </Grid2>

                {/* Summary card */}
                <Grid2 size={{ xs: 12, md: 4 }}>
                  {(billType === 'Package' || canClosePackageInvoice) ? (
                    <PackageCloserSummaryCard
                      total={totalAmount}
                      concession={concessionAmount}
                      tax={taxAmount}
                      net={netAmount}
                      creditAmount={packageCloserData?.invoice_statistics?.available_advance_amount ?? 0}
                      packageStats={{
                        packageNetAmount: packageCloserData?.invoice_statistics?.package_net_amount,
                        packagePaidAmount: packageCloserData?.invoice_statistics?.package_paid_amount,
                        packageConsumeAmount: packageCloserData?.invoice_statistics?.package_consume_amount,
                        remainingAdvanceAmount: packageCloserData?.invoice_statistics?.remaining_advance_amount,
                      }}
                    />
                  ) : (
                    <SummaryCard
                      total={totalAmount}
                      concession={concessionAmount}
                      tax={taxAmount}
                      net={netAmount}
                      consumeFromPackage={consumeFromPackageAmount}
                      packageServicesTotal={packageServicesTotal}
                      packageServiceLimit={totalServiceRemaining}
                      billType={billType}
                      isClosePackageMode={isClosePackageMode}
                      advanceBalanceAmount={totalAdvanceBalance}
                    />
                  )}
                </Grid2>
              </Grid2>

              {/* Footer actions */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const backPath = '/admin/billing/invoice/patient-invoice' + (patientId ? '/' + patientId : '');
                    // If coming from "By Services" tab, navigate back with that tab selected
                    if (fromByServices) {
                      navigate(backPath, { state: { activeTab: 'By Services' } });
                    } else {
                      navigate(backPath);
                    }
                  }}
                  sx={outlinedGrayButtonSx}
                >
                  Back to List
                </Button>
                {/* Hide Save As Draft button in package closer mode */}
                {!(billType === 'Package' || canClosePackageInvoice) && (
                  <Button
                    variant="outlined"
                    disabled={!values.services.some(svc => svc.checked) || isPaymentSubmitting}
                    onClick={async () => {
                      setShowValidationErrors(true);
                      // Validate form first before opening confirmation
                      const validationErrors = await validateForm();
                      const isValid = Object.keys(validationErrors).length === 0;
                      
                      if (isValid) {
                        // Form is valid, set action to draft and open confirmation dialog
                        setSubmitAction('draft');
                        setDraftConfirmOpen(true);
                      }
                      // If not valid, errors will be shown automatically via showValidationErrors
                    }}
                    sx={outlinedGrayButtonSx}
                  >
                    Save As Draft
                  </Button>
                )}

                {/* Collect Package Advance Button - show if there are services with packages (not for close package mode) */}
                {!canClosePackageInvoice && values.services.some(svc => svc.checked && svc.package_id) && (
                  <Button
                    variant="outlined"
                    disabled={!values.services.some(svc => svc.checked && svc.package_id) || isPaymentSubmitting}
                    onClick={handleOpenCollectPackageAdvance}
                    sx={outlinedGrayButtonSx}
                  >
                    Collect Package Advance
                  </Button>
                )}

                {/* Collect Package Advance Button - for package closer flow when user needs to pay more */}
                {billType === 'Package' && canClosePackageInvoice && (() => {
                  const creditAmount = Number(packageCloserData?.invoice_statistics?.available_advance_amount) || 0;
                  const payableAmount = Math.max(0, netAmount - creditAmount);
                  return payableAmount > 0;
                })() && (
                  <Button
                    variant="outlined"
                    disabled={
                      !values.services.some(svc => svc.checked) ||
                      !!(packageCloserData?.invoice_statistics?.package_net_amount != null && totalAmount > Number(packageCloserData.invoice_statistics.package_net_amount)) ||
                      isPaymentSubmitting
                    }
                    onClick={handleOpenCollectPackageAdvance}
                    sx={outlinedGrayButtonSx}
                  >
                    Collect Package Advance
                  </Button>
                )}

                {/* Draft Confirmation Dialog */}
                <ConfirmationModal
                  open={draftConfirmOpen}
                  handleClose={() => setDraftConfirmOpen(false)}
                  onConfirm={() => {
                    setDraftConfirmOpen(false);
                    // Pass shouldRedirect directly to avoid stale closure issue
                    handleSubmitForm(values, { shouldRedirect: true });
                  }}
                  type="save"
                  title="Save as Draft"
                  contentMessage="Are you sure you want to save this invoice as a draft? You can edit and finalize it later."
                  isConfirming={isPaymentSubmitting}
                />

                {/* Remove Service Confirmation Dialog */}
                <ConfirmationModal
                  open={removeServiceConfirmOpen}
                  handleClose={() => {
                    setRemoveServiceConfirmOpen(false);
                    setPendingRemoveIndex(null);
                  }}
                  onConfirm={() => {
                    if (pendingRemoveIndex !== null) {
                      const updated = values.services.filter((_, i) => i !== pendingRemoveIndex);
                      setFieldValue('services', updated);
                    }
                    setRemoveServiceConfirmOpen(false);
                    setPendingRemoveIndex(null);
                  }}
                  type="delete"
                  title="Remove Service"
                  contentMessage="Are you sure you want to remove this service from the invoice?"
                />

                {/* Close Package Confirmation Dialog */}
                <ConfirmationModal
                  open={freezeConfirmOpen}
                  handleClose={() => setFreezeConfirmOpen(false)}
                  onConfirm={() => {
                    setFreezeConfirmOpen(false);
                    handleFreezePackageInvoice();
                  }}
                  type="save"
                  title="Close Package"
                  contentMessage={(() => {
                    const creditAmount = Number(packageCloserData?.invoice_statistics?.available_advance_amount) || 0;
                    const refundAmount = Math.max(0, creditAmount - netAmount);
                    
                    if (refundAmount > 0) {
                      return `Are you sure you want to close this package? A refund of ₹${refundAmount.toLocaleString()} will be requested. Once closed, the invoice will be marked as paid and cannot be edited.`;
                    }
                    return 'Are you sure you want to close this package? Once closed, the invoice will be marked as paid and cannot be edited.';
                  })()}
                  isConfirming={isPaymentSubmitting}
                />

                {/* Package Full Pay Confirmation Dialog - when all services from package with sufficient advance */}
                <ConfirmationModal
                  open={packageFullPayConfirmOpen}
                  handleClose={() => setPackageFullPayConfirmOpen(false)}
                  onConfirm={() => {
                    handlePackageFullPaySave();
                  }}
                  type="save"
                  title="Save Invoice"
                  contentMessage={
                    <>
                      All services will be consumed from package advance ({currencySymbol} {consumeFromPackageAmount.toLocaleString()}).
                      <br />
                      Do you want to save this invoice?
                    </>
                  }
                  isConfirming={isPaymentSubmitting}
                />

                {/**
                 * COLLECT PAYMENT / CLOSE PACKAGE BUTTON
                 * ---------------------------------------------------------------------
                 * This button has different behaviors based on context:
                 * 
                 * FOR PACKAGE INVOICES (bill_type === 'Package'):
                 *   - Shows "Close Package" text
                 *   - If can_close_package_invoice === true: Marks invoice as paid
                 *   - If can_close_package_invoice === false: Button is disabled
                 * 
                 * FOR REGULAR INVOICES:
                 *   - Shows "Collect Payment" text
                 *   - On click: 
                 *     1. Validates form
                 *     2. If services have packages → Calls validatePackageAdvance API
                 *        a) If overall_valid === true → Opens payment modal with wallet
                 *        b) If overall_valid === false → Shows InsufficientAdvancePopup
                 *     3. If no packages → Opens payment modal directly
                 * 
                 * PACKAGE LIMIT EXCEEDED:
                 *   - Button is disabled if package services exceed service_remaining_amount
                 *   - User must remove some package services first
                 * 
                 * CHARGES EXCEED PACKAGE AMOUNT:
                 *   - Button is disabled if total charges exceed package amount
                 *   - User must reduce charges to proceed
                 * 
                 * PACKAGE CLOSER - PAYABLE AMOUNT:
                 *   - Button is disabled if payable amount > 0
                 *   - User must collect advance first using "Collect Package Advance" button
                 */}
                <Tooltip 
                  title={
                    !values.services.some(svc => svc.checked)
                      ? 'Select at least one service to proceed'
                      : isPackageLimitExceeded 
                        ? 'Total exceeds package limit — remove or uncheck package services to continue'
                        : (isDonorOrSurrogate && hasAnyPackageService && !allServicesFromPackage)
                          ? 'Invoice includes recipient\'s package items — use "Continue with Recipient" to proceed'
                          : (billType === 'Package' && packageCloserData?.invoice_statistics?.package_net_amount && totalAmount > Number(packageCloserData.invoice_statistics.package_net_amount))
                            ? 'Total charges exceed the package amount — reduce charges to proceed'
                            : (billType === 'Package' && canClosePackageInvoice && Math.max(0, netAmount - (Number(packageCloserData?.invoice_statistics?.available_advance_amount) || 0)) > 0)
                              ? 'Insufficient advance balance — collect advance before closing the package'
                              : ''
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant={isDonorOrSurrogate && billType !== 'Package' && !isFullyCoveredByPackage && !allServicesFromPackage ? 'outlined' : 'contained'}
                      color={billType === 'Package' ? 'inherit' : (isDonorOrSurrogate && billType !== 'Package' && !isFullyCoveredByPackage && !allServicesFromPackage ? undefined : 'primary')}
                      disabled={
                        isSubmitting || 
                        validatePackageAdvanceMutation.isPending || 
                        !values.services.some(svc => svc.checked) ||
                        (billType === 'Package' && !canClosePackageInvoice) ||
                        isPackageLimitExceeded ||
                        (isDonorOrSurrogate && hasAnyPackageService && !allServicesFromPackage) ||
                        (billType === 'Package' && packageCloserData?.invoice_statistics?.package_net_amount != null && totalAmount > Number(packageCloserData.invoice_statistics.package_net_amount)) ||
                        (billType === 'Package' && canClosePackageInvoice && Math.max(0, netAmount - (Number(packageCloserData?.invoice_statistics?.available_advance_amount) || 0)) > 0)
                      }
                      sx={{ 
                        fontWeight: 700,
                        ...(billType === 'Package' && {
                          backgroundColor: '#424242',
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: '#333',
                          },
                        }),
                        ...(isDonorOrSurrogate && billType !== 'Package' && !isFullyCoveredByPackage && !allServicesFromPackage && {
                          borderColor: '#bdbdbd',
                          color: '#616161',
                          '&:hover': {
                            borderColor: '#9e9e9e',
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                        }),
                      }}
                      onClick={async () => {
                        // If this is a package invoice that can be closed (via API flag)
                        // Button is only enabled when payable === 0 (advance already collected or refund/exact match)
                        if (billType === 'Package' && canClosePackageInvoice) {
                          setFormValuesForPayment(values);
                          // No payment needed (refund or exact match) - show confirmation
                          setFreezeConfirmOpen(true);
                          return;
                        }

                        // Don't proceed for Package invoices that can't be closed (button is disabled anyway)
                        if (billType === 'Package' && !canClosePackageInvoice) {
                          return;
                        }

                        // Special case: All services from package with sufficient advance
                        // No need to collect payment - save directly with package consumption
                        if (isFullyCoveredByPackage) {
                          setSubmitAction('finalize');
                          setShowValidationErrors(true);
                          
                          const validationErrors = await validateForm();
                          const isValid = Object.keys(validationErrors).length === 0;
                          
                          if (isValid) {
                            // Validate package advance first
                            const servicesWithPackages = values.services
                              .filter((svc) => svc.checked && svc.package_id)
                              .map((svc) => ({
                                package_id: svc.package_id as number,
                                amount: svc.net ?? 0,
                              }));
                            
                            const groupedPackagesMap = new Map<number, number>();
                            servicesWithPackages.forEach((svc) => {
                              const existing = groupedPackagesMap.get(svc.package_id) || 0;
                              groupedPackagesMap.set(svc.package_id, existing + svc.amount);
                            });
                            const packagesForValidation = Array.from(groupedPackagesMap.entries()).map(
                              ([package_id, amount]) => ({ package_id, amount })
                            );
                            
                            try {
                              const validationResult = await validatePackageAdvanceMutation.mutateAsync(
                                buildValidatePackageAdvanceParams(packagesForValidation)
                              );
                              
                              if (validationResult.overall_valid === true) {
                                // Package has sufficient advance - show confirmation to save
                                setFormValuesForPayment(values);
                                setPackageFullPayConfirmOpen(true);
                              } else {
                                // Insufficient advance - show popup
                                if (validationResult.packages) {
                                  setValidationResultPackages(validationResult.packages.map((pkg) => ({
                                    package_id: pkg.package_id,
                                    required_amount: pkg.required_amount || 0,
                                    balance_amount: pkg.balance_amount || 0,
                                  })));
                                }
                                setInsufficientAdvanceOpen(true);
                              }
                            } catch (err) {
                              // eslint-disable-next-line no-console
                              console.error('Package advance validation failed:', err);
                            }
                          }
                          return;
                        }

                        // Normal collect payment flow for non-Package invoices
                        setSubmitAction('finalize');
                        setShowValidationErrors(true);
                    
                        // Validate form first before opening payment modal
                        const validationErrors = await validateForm();
                        const isValid = Object.keys(validationErrors).length === 0;
                    
                        if (isValid) {
                          /**
                           * PACKAGE ADVANCE VALIDATION FLOW
                           * -----------------------------------------------------------------
                           * When services are linked to packages, we must validate that
                           * sufficient advance has been collected before allowing payment.
                           * 
                           * 1. Extract all package-linked services
                           * 2. Group by package_id and sum amounts
                           * 3. Call validatePackageAdvance API
                           * 4. Based on result:
                           *    - overall_valid === true: Open payment modal with wallet
                           *    - overall_valid === false: Show insufficient advance popup
                           */
                      
                          // Extract package IDs and amounts from selected services that have packages
                          // Also keep service name for display
                          const servicesWithPackages = values.services
                            .filter((svc) => svc.checked && svc.package_id)
                            .map((svc) => ({
                              package_id: svc.package_id as number,
                              amount: svc.net ?? 0,
                              serviceName: svc.service || svc.billing_source_name || '',
                              packageName: svc.package || '',
                            }));

                          // Group packages by package_id and sum their amounts for API validation
                          // Also create a mapping from package_id to all services using that package
                          const groupedPackagesMap = new Map<number, number>();
                          const packageIdToServicesMap = new Map<number, typeof servicesWithPackages>();
                          servicesWithPackages.forEach((svc) => {
                            const existing = groupedPackagesMap.get(svc.package_id) || 0;
                            groupedPackagesMap.set(svc.package_id, existing + svc.amount);
                        
                            // Track all services per package_id
                            const services = packageIdToServicesMap.get(svc.package_id) || [];
                            services.push(svc);
                            packageIdToServicesMap.set(svc.package_id, services);
                          });
                          const packagesForValidation = Array.from(groupedPackagesMap.entries()).map(
                            ([package_id, amount]) => ({ package_id, amount })
                          );

                          // If there are packages to validate, call the API first
                          if (packagesForValidation.length > 0) {
                            try {
                              // Debug: Log what we're sending to validation API vs what UI shows
                              if (process.env.NODE_ENV === 'development') {
                                // eslint-disable-next-line no-console
                                console.log('[Package Advance Validation] Request:', {
                                  packagesForValidation,
                                  uiAdvanceBalance: totalAdvanceBalance,
                                  uiConsumeFromPackage: consumeFromPackageAmount,
                                });
                              }
                              
                              const validationResult = await validatePackageAdvanceMutation.mutateAsync(
                                buildValidatePackageAdvanceParams(packagesForValidation)
                              );
                              
                              // Debug: Log what API returned
                              if (process.env.NODE_ENV === 'development') {
                                // eslint-disable-next-line no-console
                                console.log('[Package Advance Validation] Response:', {
                                  overall_valid: validationResult.overall_valid,
                                  packages: validationResult.packages?.map((pkg) => ({
                                    package_id: pkg.package_id,
                                    api_balance: pkg.balance_amount,
                                    required: pkg.required_amount,
                                    valid: pkg.valid,
                                  })),
                                });
                              }
                          
                              /**
                               * VALIDATION RESULT HANDLING
                               * -------------------------------------------------------------
                               * overall_valid === true:
                               *   - Package has sufficient advance to cover the consumed amount
                               *   - Package consumption is auto-debited separately
                               *   - Open payment modal for user to pay remaining amount normally
                               * 
                               * overall_valid === false:
                               *   - Package has INSUFFICIENT advance
                               *   - Show InsufficientAdvancePopup
                               *   - User must collect more advance before proceeding
                               */
                              if (validationResult.overall_valid === true) {
                                // Package has sufficient advance - consumption is handled separately (auto-debit from package)
                                // Open payment modal without pre-filling wallet - user pays remaining amount normally
                                setInitialPaymentHistory([]);
                                setWalletAdvances([]);
                            
                                // Validation successful, open collect payment modal with consume from package info
                                handleOpenCollectPayment(
                                  netAmount,
                                  patientFullName || 'Patient',
                                  patientMRN || patientId || '',
                                  values,
                                  undefined,
                                  consumeFromPackageAmount
                                );
                              } else if (validationResult.overall_valid === false) {
                                /**
                                 * INSUFFICIENT ADVANCE CASE
                                 * Opens InsufficientAdvancePopup which prompts user to:
                                 * - Acknowledge the insufficient advance
                                 * - Click "Collect Package Advance" to start 3-step advance flow
                                 */
                                setInitialPaymentHistory([]);
                            
                                // Store validation result packages with required_amount for auto-populating advance modal
                                if (validationResult.packages) {
                                  const storedPackages = validationResult.packages.map((pkg) => ({
                                    package_id: pkg.package_id,
                                    required_amount: pkg.required_amount || 0,
                                    balance_amount: pkg.balance_amount || 0,
                                  }));
                                  if (process.env.NODE_ENV === 'development') {
                                    // eslint-disable-next-line no-console
                                    console.log('Storing validation result packages:', storedPackages);
                                  }
                                  setValidationResultPackages(storedPackages);
                                }
                            
                                setInsufficientAdvanceOpen(true);
                              } else {
                                setInitialPaymentHistory([]);
                                // No mapped advances but validation passed, open collect payment
                                handleOpenCollectPayment(
                                  netAmount,
                                  patientFullName || 'Patient',
                                  patientMRN || patientId || '',
                                  values,
                                  undefined,
                                  0
                                );
                              }
                            } catch (err) {
                              // eslint-disable-next-line no-console
                              console.error('Package advance validation failed:', err);
                              // Optionally show an error message to the user
                            }
                          } else {
                            // No packages to validate, open collect payment modal directly
                            handleOpenCollectPayment(
                              netAmount,
                              patientFullName || 'Patient',
                              patientMRN || patientId || '',
                              values,
                              undefined,
                              0
                            );
                          }
                        }
                        // If not valid, errors will be shown automatically via showValidationErrors
                      }}
                    >
                      {billType === 'Package' 
                        ? (() => {
                          const creditAmount = Number(packageCloserData?.invoice_statistics?.available_advance_amount) || 0;
                          const refundAmount = Math.max(0, creditAmount - netAmount);
                          if (refundAmount > 0) return 'Close Package';
                          return 'Close Package';
                        })()
                        : isFullyCoveredByPackage 
                          ? 'Save'
                          : isDonorOrSurrogate && !allServicesFromPackage
                            ? 'Continue with Donor'
                            : 'Collect Payment'}
                    </Button>
                  </span>
                </Tooltip>

                {/* Continue with Recipient button - only for donor/surrogate patients */}
                {/* Hidden when all services are from a package — the package already identifies the recipient */}
                {isDonorOrSurrogate && billType !== 'Package' && !allServicesFromPackage && (
                  <Button
                    variant="contained"
                    disabled={
                      isSubmitting || 
                      !values.services.some(svc => svc.checked) ||
                      isPackageLimitExceeded
                    }
                    sx={{ 
                      fontWeight: 700,
                      backgroundColor: '#424242',
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: '#333',
                      },
                    }}
                    onClick={async () => {
                      setSubmitAction('finalize');
                      setShowValidationErrors(true);
                      const validationErrors = await validateForm();
                      const isValid = Object.keys(validationErrors).length === 0;
                      if (isValid) {
                        setPendingFormValues(values);
                        // Fetch recipients for this donor
                        setRecipientsLoading(true);
                        try {
                          const donorId = patientId ? Number(patientId) : currentPatient?.id;
                          if (donorId) {
                            const data = await fetchLinkedRecipients(donorId);
                            const recipientList = data?.recipients ?? [];
                            setRecipients(recipientList);
                            if (recipientList.length === 1) {
                              setSelectedRecipientId(recipientList[0].id);
                            } else {
                              setSelectedRecipientId(null);
                            }
                          }
                        } catch (err) {
                          // eslint-disable-next-line no-console
                          console.error('Failed to fetch recipients:', err);
                          setRecipients([]);
                        } finally {
                          setRecipientsLoading(false);
                        }
                        setRecipientModalOpen(true);
                      }
                    }}
                  >
                    Continue with Recipient
                  </Button>
                )}
              </Box>

              {/* Package Services Modal - inside Formik to access setFieldValue */}
              <PackageServicesModal
                open={packageModalOpen}
                onClose={() => setPackageModalOpen(false)}
                onSave={(selectedRows: PackageServiceRow[]) => {
                  
                  // map PackageServiceRow -> Service
                  const mapped = selectedRows.map<Service>((r) => {
                    const charges = r.unitRate;
                    const concessionPercent = r.discountPercent ?? 0;
                    // Use discount_amount from API if available, otherwise calculate from percent
                    const concessionAmount = r.discount_amount ?? ((concessionPercent / 100) * charges);
                    const net = charges - concessionAmount;
                    
                    return {
                      date: getTodayYMD(),
                      code: r.serviceCode || (r.serviceId != null ? String(r.serviceId) : ''),
                      service: r.serviceName,
                      package: selectedPackageName,
                      package_id: selectedPackageId ? Number(selectedPackageId) : undefined,
                      provider: r.providerId != null ? String(r.providerId) : '',
                      charges,
                      concession: { amount: concessionAmount, percent: concessionPercent },
                      tax: { amount: 0, percent: 0 },
                      net,
                      checked: true,
                      isFromSearch: false,
                      is_from_package: true,
                      is_from_consumption: true,
                      billing_source_type: r.serviceSourceType || 'SERVICE',
                      billing_source_id: r.serviceId ? Number(r.serviceId) : undefined,
                      billing_source_code: r.serviceCode,
                      billing_source_name: r.serviceName,
                    };
                  });

                  // Use ref to get the LATEST Formik services (not stale closure)
                  const currentServices = formikServicesRef.current;
                  const updatedServices = [...currentServices, ...mapped];
                  // Update both Formik AND React state to prevent enableReinitialize from resetting
                  setFieldValue('services', updatedServices);
                  setServices(updatedServices);

                  setPackageModalOpen(false);
                }}
                packageName={selectedPackageName}
                headerSummary={undefined}
                rows={[]}
                packageId={selectedPackageId ?? ''}
                billingSourceIds={
                  // Get billing_source_ids from all services in the invoice
                  values.services
                    .filter((svc) => svc.billing_source_id != null)
                    .map((svc) => Number(svc.billing_source_id))
                }
                currentPackageServices={
                  // Filter services from this package that came from consumption modal
                  values.services
                    .filter(
                      (svc) =>
                        svc.is_from_consumption === true &&
                        svc.package_id != null &&
                        String(svc.package_id) === String(selectedPackageId)
                    )
                    .map<CurrentPackageService>((svc) => ({
                      billing_source_id: svc.billing_source_id,
                      serviceCode: svc.billing_source_code,
                    }))
                }
              />
            </form>
          );
        }}
      </Formik>

      {/**
       * =======================================================================
       * MODALS SECTION
       * =======================================================================
       * 
       * 1. CollectPaymentModal (Regular Invoice Payment)
       *    - Opens after successful package validation or for non-package invoices
       *    - Shows payment methods (cash, card, UPI, wallet, etc.)
       *    - Wallet section pre-filled when package advance is available
       * 
       * 2. CollectPackageAdvanceModal (Step 1 of 3-Step Flow)
       *    - Opens from "Collect Package Advance" button or InsufficientAdvancePopup
       *    - User selects package and enters advance amount
       * 
       * 3. CollectPaymentModal (Step 2 of 3-Step Flow)
       *    - Opens after completing Step 1
       *    - User selects payment method for the advance
       *    - Wallet is disabled (showWallet=false)
       * 
       * 4. InsufficientAdvancePopup
       *    - Shows when validatePackageAdvance returns overall_valid === false
       *    - Prompts user to collect more advance before proceeding
       */}
      
      {/* Regular Invoice Payment Modal - opens after validation or for non-package services */}
      {paymentDetailsForModal && (
        <CollectPaymentModal
          open={collectPaymentOpen}
          onClose={() => {
            setCollectPaymentOpen(false);
            // Cleanup wallet state when modal closes
            setInitialPaymentHistory([]);
            setConsumeFromPackageForModal(0);
          }}
          onSave={handlePaymentSave}
          paymentDetails={paymentDetailsForModal}
          existingPaymentHistory={initialPaymentHistory}
          walletAdvances={walletAdvances}
          paymentModes={paymentModes}
          upiProviders={upiProviders}
          showWallet={true}  // Enable wallet to pay remaining amount (non-package services)
          maxCreditAmount={maxBillCreditAmount}
          consumeFromPackage={consumeFromPackageForModal}
          isSubmitting={isPaymentSubmitting}
        />
      )}

      {/**
       * PACKAGE ADVANCE 3-STEP FLOW
       * Step 1: User fills advance form (package, amount, date, remarks)
       * Step 2: User selects payment method
       * Step 3: Save happens in handleSavePackageAdvance
       */}
      
      {/* Step 1: Package Advance Form - select package & enter amount */}
      {packageOptionsForAdvance.length > 0 && (
        <CollectPackageAdvanceModal
          open={collectPackageAdvanceOpen}
          onClose={() => {
            setCollectPackageAdvanceOpen(false);
            // Don't clear packageOptionsForAdvance here - user might reopen
          }}
          onCollectPayment={handlePackageAdvanceCollectPayment}  // Transitions to Step 2
          packages={packageOptionsForAdvance}
          initialSelectedPackageId={selectedDonorPackage?.id ?? packageId}  // Pre-select donor's chosen package or package closure package
        />
      )}

      {/* Step 2: Payment Methods - select how to pay the advance  */}
      {packageAdvancePaymentDetails && (
        <CollectPaymentModal
          open={collectPackagePaymentOpen}
          onClose={() => {
            setCollectPackagePaymentOpen(false);
            // Clear payment details but preserve form data for potential retry
            setPackageAdvancePaymentDetails(null);
            // Optionally reopen Step 1 if user cancels:
            // setCollectPacskageAdvanceOpen(true);
          }}
          paymentType="ADVANCE"
          onSave={handleSavePackageAdvance}  // Step 3: Final save
          paymentDetails={packageAdvancePaymentDetails}
          paymentModes={paymentModes}
          upiProviders={upiProviders}
          hidePaymentModeToggle={true}
          showWallet={false}  // Wallet disabled for collecting advance itself
          existingPaymentHistory={[]}
          walletAdvances={[]}
          isSubmitting={isAdvanceSubmitting}
        />
      )}

      {/**
       * INSUFFICIENT ADVANCE POPUP
       * Shown when package advance validation fails (overall_valid === false).
       * On confirm, opens the 3-step package advance collection flow.
       */}
      <InsufficientAdvancePopup
        open={insufficientAdvanceOpen}
        onClose={() => setInsufficientAdvanceOpen(false)}
        onCollectAdvance={handleOpenCollectPackageAdvanceExternal}
      />

      {/* Recipient Selection Modal - shown when donor/surrogate clicks "Continue with Recipient" */}
      <RecipientSelectionModal
        open={recipientModalOpen}
        onClose={() => setRecipientModalOpen(false)}
        recipients={recipients}
        selectedRecipientId={selectedRecipientId}
        onSelectRecipient={setSelectedRecipientId}
        loading={recipientsLoading}
        onProceed={handleRecipientProceed}
      />
    </>
  );
};

export default NewInvoiceForm;
